import { useEffect, useMemo, useState, useRef } from "react";
import { Box, TextField, Typography, ToggleButton, ToggleButtonGroup, FormControl, Select, MenuItem, Alert, IconButton, Button, Checkbox, FormControlLabel, Autocomplete } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { convertDateTimeToInputDateString } from "../../utils/datetimeUtils";
import handleAgentName from "../../utils/userUtils";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { icBirthday, icAge } from "../../utils/icBirthday";
import { getAllCities } from "../../api/cityApi";
import { generateInvitation, getMyInvitations } from "../../api/invitationApi";
import { getAddons, getPricingHistories, getPricingHistoryItems, getPricing } from "../../api/resourceApi";
import { computeSizing } from "../../utils/rscSizing";
import { methodBreakdown, termForMethod, ccFromCash, isMixMethod, mixBreakdown, MIX_TERM_OPTIONS } from "../../utils/ccPricing";
import CcBankSelect from "./ccBankSelect";
import { priceRange as ccPriceRange } from "../../utils/ccBanks";
import { factorFor, roundUp10 as ccRoundUp10 } from "../../utils/ccPricing";
import { sstOn } from "../../utils/sst";
import { backupboxRemoval, suriaHomeBonus, PER_BATTERY_PROMO } from "../../utils/rscPromos";

/* A–Z BY WHAT IS ON SCREEN. These lists arrive in whatever order the API returns — by
   user id, effectively creation order — so finding a name means reading the whole list.
   Sorted on the COMPOSED display name, not the raw first/last, because that is the
   string the agent is scanning for. localeCompare so case and accents sort sensibly
   instead of by code point. (user 2026-10-31: "can help me make it ascending order
   with a-z?") */
const byName = (nameOf) => (a, b) => String(nameOf(a) || '').localeCompare(String(nameOf(b) || ''), undefined, { sensitivity: 'base' });
const agentDisplay = (o) => (handleAgentName(o.firstName, o.lastName, o.alias) || '').trim() || o.username || `User #${o.userId}`;

// CC = cash ÷ factor, rounded UP to RM 10 (latest rates 2026-06-17).
const CC_FACTORS_GF = { 12: 0.955, 24: 0.935, 36: 0.925, 60: 0.9 };
const ccFromCashGF = (cash, term) => {
    const c = Number(cash) || 0;
    if (c <= 0) return 0;
    return Math.ceil(c / (CC_FACTORS_GF[term] || 0.925) / 10) * 10;
};

const sectionTitle = (text) => (
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 2.5, mb: 1.5, pb: 0.5, borderBottom: '1px solid #E2E8F0', fontFamily: "'Poppins', sans-serif" }}>
        {text}
    </Typography>
);

/**
 * "Invite a referrer" — the agent's own link, copied to the clipboard.
 *
 * The token already exists: every agent carries an auto-generated Referral invitation.
 * This only fetches it and copies it, so a link shared from here is the same link the
 * Profile page shows, and approving the application works exactly as it does today.
 *
 * Deliberately quiet — a text button under the picker, not a call to action. Most sales
 * have no referrer and the control should not suggest they ought to.
 */
function InviteReferrerButton({ userId }) {
    const [state, setState] = useState('idle');   // idle | working | copied | failed

    const invite = async () => {
        if (!userId) return;
        setState('working');
        try {
            // Reuse an existing Active referral invitation rather than minting one per
            // click: generating a fresh token each time would quietly invalidate a link
            // the agent had already sent to somebody.
            let token = null;
            try {
                const mine = (await getMyInvitations(userId))?.data || [];
                token = mine.find(i => i.status === 'Active' && i.intendedRole === 'Referral')?.token || null;
            } catch { /* fall through to generating one */ }

            if (!token) {
                const made = await generateInvitation({ inviterId: userId, intendedRole: 'Referral' });
                token = made?.data?.token || null;
            }
            if (!token) { setState('failed'); return; }

            const link = `${window.location.origin}/join/${token}`;
            try {
                await navigator.clipboard.writeText(link);
            } catch {
                // Clipboard is blocked outside a secure context, which is every phone
                // opening this over plain http on the tailnet. Fall back to a selection
                // the person can copy by hand rather than failing silently.
                const ta = document.createElement('textarea');
                ta.value = link;
                ta.style.cssText = 'position:fixed;opacity:0';
                document.body.appendChild(ta); ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setState('copied');
            setTimeout(() => setState('idle'), 2500);
        } catch {
            setState('failed');
        }
    };

    return (
        <Button size="small" onClick={invite} disabled={state === 'working' || !userId}
            sx={{ textTransform: 'none', fontSize: '0.72rem', px: 0.5, minWidth: 0, alignSelf: 'start' }}>
            {state === 'working' ? 'Getting your link…'
                : state === 'copied' ? 'Link copied — send it to them'
                : state === 'failed' ? 'Could not get the link — try your Profile page'
                : 'Not listed? Invite a referrer'}
        </Button>
    );
}

const fieldLabel = (text, required) => (
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
        {text} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </Typography>
);

export default function GenericSalesForm({
    mode = "create",
    isSuperAdmin = false,
    agentGroup, agentName, agentNameOptions = [], selectedAgentId, setSelectedAgentId,
    coBrokeGroupName, coBrokeAgentName,
    coBrokeGroupOptions = [], coBrokeNameOptions = [],
    selectedCoBrokeGroupId, selectedCoBrokeAgentId, setSelectedCoBrokeAgentId,
    // Co-broke fields can be unlocked independently of the main agent (e.g. the
    // admin "Adjustment Edit": agent stays locked, co-broke becomes editable).
    // Defaults fall back to the agent flags so create-sales is unchanged.
    disableCoBrokeFields, handleCoBrokeGroupChange,
    referralOptions = [], selectedReferralId, setSelectedReferralId, disableReferralField = false,
    onCreateReferral,
    // Who bears the referral fee. Default Astern; only an admin may change it, because
    // an agent choosing not to pay for their own referral is not a choice to offer.
    referralPaidByAstern = true, setReferralPaidByAstern = null, canSetReferralPayer = false,
    // Whose invite link to hand out when the agent wants a referrer of their own.
    currentUserId = null,
    customerName, setCustomerName, customerEmail, setCustomerEmail,
    customerPhone, setCustomerPhone, customerAddress, setCustomerAddress,
    customerIc,
    customerState, setCustomerState, customerCity, setCustomerCity, customerPostcode, setCustomerPostcode,
    selectedCategory, setSelectedCategory, phaseMode = 'single', setPhaseMode = () => {}, panelQty, setPanelQty, energy, setEnergy,
    // Pricing snapshot picker (residential): null = live list, else a historyId.
    // atapPanelWatt = the ACTIVE list's panel wattage (kWp ÷ panels) — current or old —
    // so the wattage field follows the selected ATAP pricing (current 650W / old 620W).
    pricingSnapshotId = null, setPricingSnapshotId = () => {}, pricingSnapshots = [], atapPanelWatt = null,
    // Site Visit answers — given by the AGENT at the sale, because they are standing at
    // the property and know. Joyce weeks later has to ring and ask. (user 2026-10-11)
    siteGatedGuarded = false, setSiteGatedGuarded = null,
    mgmtApprovalNeeded = false, setMgmtApprovalNeeded = null,
    siteVisitRequired = true, setSiteVisitRequired = null,
    availablePaymentMethods = [], paymentMethod, setPaymentMethod,
    // The card's bank (60-month rates differ by bank). Absent setCcBank = the caller
    // has not adopted it yet, and the field simply does not render.
    ccBank = '', setCcBank, ccBankMethod = '', setCcBankMethod,
    // Wiring cable brand for the quotation "Balance of System" line (Any / KUKA Cable / HELUKABEL).
    cableBrand = '', setCableBrand = () => {},
    originalPrice, setOriginalPrice, canEditPrice = false, residentialMatch,
    // Edit-sales "edit-in-place" two-box mode (privileged editor only):
    //   Original Sales Price = the payment-method/stored basis that DRIVES COMMISSION
    //     (originalPrice here holds the stored value directly — saved without conversion).
    //   Original Cash Price (ocp) = ATAP-list reference; warns if OSP ≠ derive(OCP).
    // Default off → create-sales / RSC keep the single cash box + conversion-on-save.
    salesPriceLens = false, ocp = '', setOcp, onCashRef,
    // Reports the LIVE "Total Quotation" (Final + Add-on in the paying basis — Cash / CC / MIX)
    // up to the parent so the payment-progress bar's denominator updates as pricing is edited.
    // (user 2026-07-22)
    onQuotationTotal,
    // saleTouched: edit page passes true once the user actually changes a field. The CC add-on
    // converts only when true (or in create mode) — so just VIEWING an old sale shows its stored
    // (flat) total, and it converts the moment you edit. (user 2026-07-10)
    saleTouched = false,
    // Original Cash Price is commission-driving — only Accountant / Super Admin may edit
    // it. Everyone else (Group Admin, agents) sees it greyed/read-only. Defaults open so
    // other callers are unchanged. (user 2026-07-01)
    canEditCashPrice = true,
    // Item 5 — direct CC-pricing override (Super Admin / Accountant). When set, the CC
    // card's four figures become editable inputs and the sale stores them verbatim
    // (commission = Original − Astern). null = auto-derive from the cash anchor (display only).
    ccOverride = null, setCcOverride, canEditCcPricing = false,
    // MIX (cash + CC): the customer pays `mixCashAmount` in cash, the rest is financed on
    // `mixTerm` months. Only the customer TOTAL/Final change (÷factor on the CC portion) —
    // commission stays Original − Astern in cash. (user 2026-07-22)
    mixCashAmount = '', setMixCashAmount = () => {}, mixTerm = 36, setMixTerm = () => {},
    finalPrice, addOnPrice, setAddOnPrice, depositOn, setDepositOn,
    asternDiscount, setAsternDiscount, agentDiscount, setAgentDiscount,
    // Upline/Team discount (2026-08-19). Display-only here — the inputs live in the
    // parent (createSalesForm / editSalePage / editAdminSalesModal). These feed the
    // Section-2 price card so the Final/Total shown while keying matches the saved
    // FinalPrice. Defaulted to 0 so callers that pass nothing behave exactly as before.
    uplineDiscount = 0, teamDiscount = 0, setUplineDiscount, setTeamDiscount,
    deductSst = false, setDeductSst,
    discountFundedBy, setDiscountFundedBy,
    agentDiscountContribution, setAgentDiscountContribution,
    uplineDiscountContribution, setUplineDiscountContribution,
    disableAgentFields = false, disableCustomerFields = false,
    disableProductFields = false, disablePricingFields = false,
    disableDepositDate = false,
    // RSC-style promo auto-discount (manual create/edit only)
    showPromos = false,
    promoBackupbox = false, setPromoBackupbox,
    promoFirst640 = false, setPromoFirst640,
    promoPerBatt = false, setPromoPerBatt,
    handleAgentGroupChange,
    // Add-on table
    lineItems = [], setLineItems, canEditAddons = false,
    // Saved (Section-2) pricing for an existing sale — the method-basis values that
    // were stored at create. Shown read-only when Section 1 (cash) is left blank on
    // edit, so a sale's payment-method pricing REMAINS visible after creation.
    savedPricing = null, savedPaymentMethod,
    // RSC-style sizing validity — called with the sizing verdict so the parent can
    // block create/edit when the config is invalid (over-SEDA without CCC, or the
    // safety ratio is breached), exactly like the RSC calculator gates generation.
    onSizingChange,
    // Lock the promo checkboxes (read-only). The edit page sets this true once a sale
    // is Active/signed (price frozen) and false while Pending (still adjustable).
    promosLocked = false,
    // Seed the panel-wattage dropdown (UI-only). Used by the RSC → Create-Sales
    // hand-off so the RSC package's wattage carries over. Defaults to 640 when unset.
    initialPanelWattage,
    // True when this create form was seeded from the RSC calculator. RSC has already
    // baked promo-adjusted prices into the line items, so the promo auto-effects must
    // NOT re-derive them on mount (see promoTouchedRef below). (user 2026-08-01)
    rscSeeded = false,
}) {
    const inputSx = { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' };
    const PP = "'Poppins', sans-serif";
    // The small grey line under a field. One definition, so the four fields in the
    // product row cannot drift apart again.
    const helperSx = { fontSize: '0.65rem', color: '#94A3B8', mt: 0.5, fontFamily: PP, lineHeight: 1.35 };

    // True only after the user DELIBERATELY toggles a promo checkbox in this session.
    // Gates every promo-driven auto-calc below so they NEVER fire on mount/edit-load —
    // that would clobber a sale's frozen Astern discount / battery price (price-freeze
    // rule). See feedback_price_freeze_no_retroactive_atap.
    // In CREATE mode the promos are active (default-ticked) from the start, so allow
    // their auto-fill/row effects to run immediately — there is no frozen sale price
    // to protect. In EDIT mode it stays false until the user deliberately toggles a
    // promo (price-freeze rule).
    // EXCEPTION — RSC hand-off: when create-sales is seeded from the RSC calculator
    // (rscSeeded), RSC has ALREADY baked the correct promo-adjusted prices into the
    // line items + Astern discount. Starting "untouched" here stops the promo effects
    // from re-deriving (and clobbering) RSC's data-driven battery net etc. on mount;
    // they resume only if the user deliberately toggles a promo checkbox. This keeps
    // "create sales always follows RSC" true for any ATAP price. (user 2026-08-01)
    const promoTouchedRef = useRef(mode === 'create' && !rscSeeded);

    // ── Add-on catalog ────────────────────────────────────────────────
    const [addonCatalog, setAddonCatalog] = useState([]);
    useEffect(() => {
        getAddons()
            .then(r => setAddonCatalog(r.data || []))
            .catch(() => {});
    }, []);
    // Backfill catalog descriptions onto line items that were reloaded from the DB
    // without one (SalesLineItem doesn't persist the description) so the quotation
    // still renders the description bullets. One-shot, name-matched.
    const descBackfilledRef = useRef(false);
    useEffect(() => {
        if (descBackfilledRef.current || !setLineItems) return;
        if (!(addonCatalog || []).length || !(lineItems || []).length) return;
        let changed = false;
        const next = lineItems.map(li => {
            if (li.isBattery) return li;
            const cat = addonCatalog.find(a => (a.itemName || '').trim() === (li.itemName || '').trim());
            if (!cat) return li;
            let out = li;
            // Restore the description (SalesLineItem doesn't persist it).
            if (cat.description && !li.description) { out = { ...out, description: cat.description }; changed = true; }
            // Restore the base+rate split (priceBase/priceRate aren't persisted) so the
            // base box + per-unit rate show. Keep the FROZEN line total: rate comes from
            // the catalog, base = storedTotal − rate×qty (so the total never changes).
            const ps = String(cat.price ?? '').replace(/[^\d.+]/g, '');
            if (ps.includes('+') && !(Number(out.priceRate) > 0)) {
                const rate = Number(ps.split('+')[1]) || 0;
                const q = Number(out.qty) || 0;
                const total = Number(out.lineTotal) || 0;
                const base = +(total - rate * q).toFixed(2);
                out = { ...out, priceRate: rate, priceBase: base, unitPrice: rate };
                changed = true;
            }
            return out;
        });
        descBackfilledRef.current = true;
        if (changed) setLineItems(next);
    }, [addonCatalog, lineItems, setLineItems]);

    // Pricing-version validity note: match the sale's deposit date to the pricing
    // history version that was active then (ValidFrom ≤ depositOn ≤ ValidUntil).
    const [pricingHistories, setPricingHistories] = useState([]);
    useEffect(() => {
        getPricingHistories().then(r => setPricingHistories(r.data || [])).catch(() => {});
    }, []);
    const activePricing = useMemo(() => {
        if (!(pricingHistories || []).length) return null;
        // Match against the deposit date when set; otherwise fall back to the
        // latest price list (the version active today) so the note always maps
        // to a price list even before a deposit date is entered.
        const dep = depositOn ? new Date(depositOn) : new Date();
        dep.setHours(0, 0, 0, 0);
        if (isNaN(dep.getTime())) return null;
        const match = pricingHistories.find(h => {
            const from = new Date(h.validFrom); from.setHours(0, 0, 0, 0);
            const until = h.validUntil ? new Date(h.validUntil) : null;
            if (until) until.setHours(0, 0, 0, 0);
            return dep >= from && (!until || dep <= until);
        });
        if (match) return match;
        // No window contains the date — fall back to the most recent list by
        // ValidFrom so a sale dated past the last range still shows the latest.
        return [...pricingHistories]
            .sort((a, b) => new Date(b.validFrom) - new Date(a.validFrom))[0] || null;
    }, [depositOn, pricingHistories]);
    const fmtPriceDate = (d) => new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    // The current/latest price list = the one with the most recent ValidFrom. It is
    // always treated as effective "to present" (a stale ValidUntil shouldn't make the
    // live list look expired), and dated from when it was created.
    const latestHistoryId = useMemo(() => {
        if (!(pricingHistories || []).length) return null;
        return [...pricingHistories].sort((a, b) => new Date(b.validFrom) - new Date(a.validFrom))[0]?.historyId ?? null;
    }, [pricingHistories]);
    // Pull the price list ITEMS for the version active on the deposit date, so the
    // reference uses the price that was valid then (not the live price).
    const [activeItems, setActiveItems] = useState([]);
    useEffect(() => {
        if (activePricing?.historyId) {
            getPricingHistoryItems(activePricing.historyId).then(r => setActiveItems(r.data || [])).catch(() => setActiveItems([]));
        } else {
            setActiveItems([]);
        }
    }, [activePricing]);
    // Cash price (single/3p) from the deposit-date version, matched by panel count.
    const histRefCash = useMemo(() => {
        if (!activeItems.length || !panelQty) return 0;
        const row = activeItems.find(i => (i.listName || '').toLowerCase().includes('residential') && Number(i.panelQty) === Number(panelQty));
        if (!row) return 0;
        return phaseMode === '3phase'
            ? (Number(row.cash3p8k) || Number(row.cashPrice) || 0)
            : (Number(row.cashPrice) || Number(row.cash3p8k) || 0);
    }, [activeItems, panelQty, phaseMode]);

    // LIVE Residential price list — used so the Price Reference matches what RSC / the
    // current price table actually charge (the deposit-date history snapshot can drift
    // stale when the live list is edited without re-snapshotting the history version).
    const [livePricing, setLivePricing] = useState([]);
    useEffect(() => {
        getPricing().then(r => setLivePricing((r.data || []).filter(p => (p.listName || '').toLowerCase().includes('residential')))).catch(() => {});
    }, []);
    const liveRefCash = useMemo(() => {
        if (!livePricing.length || !panelQty) return 0;
        const row = livePricing.find(p => Number(p.panelQty) === Number(panelQty));
        if (!row) return 0;
        return phaseMode === '3phase'
            ? (Number(row.cash3p8k) || Number(row.cashPrice) || 0)
            : (Number(row.cashPrice) || Number(row.cash3p8k) || 0);
    }, [livePricing, panelQty, phaseMode]);

    // THE authoritative CASH anchor = the ATAP dated-list price (same source as the blue
    // Price-Reference box): live list for the latest version, the deposit-date snapshot for
    // a back-dated sale, else the residential match. NEVER back-derived from a stored value
    // (that basis is ambiguous and could be polluted) — 0 means "no reliable cash" → save
    // leaves the sale's pricing untouched. The edit page consumes this via onCashRef.
    const cashRef = useMemo(() => {
        const isLatest = !activePricing || activePricing.historyId === latestHistoryId;
        const pick = isLatest ? (liveRefCash > 0 ? liveRefCash : histRefCash)
                              : (histRefCash > 0 ? histRefCash : liveRefCash);
        if (pick > 0) return pick;
        return residentialMatch
            ? ((phaseMode === '3phase' ? (Number(residentialMatch.cash3p8k) || Number(residentialMatch.cashPrice)) : (Number(residentialMatch.cashPrice) || Number(residentialMatch.cash3p8k))) || 0)
            : 0;
    }, [activePricing, latestHistoryId, liveRefCash, histRefCash, residentialMatch, phaseMode]);
    useEffect(() => { if (onCashRef) onCashRef(cashRef); }, [cashRef, onCashRef]);

    // Compute add-on total from line items and keep addOnPrice in sync — but ONLY
    // when there ARE line items. Old sales (no line items) carry a lump-sum
    // Add-on Price that must be PRESERVED, not overwritten to 0; the agent edits
    // that field manually instead.
    const hasLineItems = (lineItems || []).length > 0;
    const addonTotal = useMemo(
        () => (lineItems || []).reduce((sum, r) => sum + (Number(r.lineTotal) || 0), 0),
        [lineItems]
    );
    // Once a sale is line-item-driven, keep addOnPrice in sync — INCLUDING dropping it
    // to 0 when the last line item is removed (otherwise it stayed stale and the removed
    // battery's price lingered). Sales that never had line items (old lump-sum) keep
    // their manually-entered Add-on Price untouched.
    const everHadLineItems = useRef(false);
    useEffect(() => { if (hasLineItems) everHadLineItems.current = true; }, [hasLineItems]);
    useEffect(() => {
        if (setAddOnPrice && (hasLineItems || everHadLineItems.current)) setAddOnPrice(addonTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addonTotal, hasLineItems]);

    // Report the LIVE "Total Quotation" (the customer-paying total = Final + Add-on in the
    // paying basis — Cash / CC ÷factor / MIX) up to the parent so the Cust-Progress /
    // payment-documents denominator follows it as pricing is edited. The pricing block writes
    // _qTotalRef during render; this effect (runs after every render) reports it on change.
    // (user 2026-07-22)
    const _qTotalRef = useRef(0);
    const _qReportedRef = useRef(null);
    useEffect(() => {
        if (onQuotationTotal && _qTotalRef.current !== _qReportedRef.current) {
            _qReportedRef.current = _qTotalRef.current;
            onQuotationTotal(_qTotalRef.current);
        }
    });

    // helpers for editing rows
    const updateRow = (idx, patch) => {
        if (!setLineItems) return;
        setLineItems(prev => {
            const next = prev.map((r, i) => {
                if (i !== idx) return r;
                const merged = { ...r, ...patch };
                const q = Number(merged.qty) || 0;
                if ('priceBase' in patch || 'priceRate' in patch) {
                    // Edit of the base box or the per-unit rate on a base+rate add-on:
                    // unitPrice = rate; total = base + rate × qty.
                    merged.unitPrice = Number(merged.priceRate) || 0;
                    merged.lineTotal = +((Number(merged.priceBase) || 0) + (Number(merged.priceRate) || 0) * q).toFixed(2);
                } else if ('unitPrice' in patch) {
                    // Manual unit-price edit on a FLAT item → drop any base+rate.
                    merged.priceBase = 0; merged.priceRate = 0;
                    merged.lineTotal = +(q * (Number(merged.unitPrice) || 0)).toFixed(2);
                } else if (Number(merged.priceRate) > 0) {
                    // qty change on a base+rate add-on (Optimizer "700 base + 170/pc"):
                    // unitPrice stays the per-pc RATE; total = base + rate × qty.
                    merged.unitPrice = Number(merged.priceRate);
                    merged.lineTotal = +(Number(merged.priceBase || 0) + Number(merged.priceRate) * q).toFixed(2);
                } else {
                    merged.lineTotal = +(q * (Number(merged.unitPrice) || 0)).toFixed(2);
                }
                return merged;
            });
            return next;
        });
    };
    const removeRow = (idx) => {
        if (!setLineItems) return;
        setLineItems(prev => prev.filter((_, i) => i !== idx));
    };
    const addFromCatalog = (addonId) => {
        if (!setLineItems || !addonId) return;
        const addon = addonCatalog.find(a => String(a.addonId) === String(addonId));
        if (!addon) return;
        // Parse price — may be "700+170" (base+rate, e.g. Optimizer RM700 + RM170/pc)
        // or plain "1500". For base+rate keep base + rate so changing qty (pcs) does
        // base + rate×qty (matches the RSC), not a flat unit × qty.
        const priceStr = String(addon.price ?? '').replace(/[^\d.+]/g, '');
        let priceBase = 0, priceRate = 0, unitPrice = 0;
        if (priceStr.includes('+')) {
            const [base, rate] = priceStr.split('+');
            priceBase = Number(base) || 0;
            priceRate = Number(rate) || 0;
            unitPrice = priceRate;   // show the per-unit RATE (base is added once, via lineTotal)
        } else {
            unitPrice = Number(priceStr) || 0;
        }
        const qty = 1;
        const lineTotal = priceRate > 0 ? +(priceBase + priceRate * qty).toFixed(2) : +(qty * unitPrice).toFixed(2);
        setLineItems(prev => [
            ...prev,
            {
                salesLineItemId: 0,
                itemName: addon.itemName,
                category: addon.category || null,
                description: addon.description || '',   // shown as bullets on the quotation
                isBattery: false,
                qty,
                batteryKwh: null,
                priceBase,
                priceRate,
                unitPrice,
                lineTotal,
                sortOrder: prev.length,
            },
        ]);
    };

    // Battery add-on: ATAP-priced 16 kWh modules @ RM8600 each (current 16 kWh ATAP
    // cash price — MUST match RSC's data-driven battUnitPrice so the create form's
    // net = RSC's net, e.g. 8600 − 800 = 7800), up to 10×16 = 160 kWh. Commercial
    // sales adjust the unit price manually (commercial batteries are per-kWh, not flat).
    const BATTERY_UNIT_KWH = 16, BATTERY_UNIT_PRICE = 8600, PER_BATTERY_DISCOUNT = PER_BATTERY_PROMO;  // rscPromos single source (RM800 — reverted 2026-08-13)
    const batteryKwhOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * BATTERY_UNIT_KWH); // 16..160
    const [batteryKwhSel, setBatteryKwhSel] = useState('');
    // Net per-16kWh-battery price: base less the RM800 per-battery promo when on.
    const battNetPrice = (showPromos && promoPerBatt) ? (BATTERY_UNIT_PRICE - PER_BATTERY_DISCOUNT) : BATTERY_UNIT_PRICE;
    // Every battery is a single 16 kWh module; the selected total kWh just decides
    // how many modules (qty). The line is always named "Battery 16kWh" so the name
    // stays correct when the qty is edited. Adding more increments the qty.
    const addBattery = (kwh) => {
        const k = Number(kwh) || 0;
        if (!setLineItems || k <= 0) return;
        const units = Math.max(1, Math.ceil(k / BATTERY_UNIT_KWH));
        setLineItems(prev => {
            const idx = prev.findIndex(r => r.isBattery);
            if (idx >= 0) {
                const next = [...prev];
                const newQty = (Number(next[idx].qty) || 0) + units;
                next[idx] = {
                    ...next[idx],
                    qty: newQty,
                    unitPrice: battNetPrice,
                    lineTotal: +(newQty * battNetPrice).toFixed(2),
                };
                return next;
            }
            return [...prev, {
                salesLineItemId: 0,
                itemName: `Battery ${BATTERY_UNIT_KWH}kWh`,
                category: 'Battery',
                isBattery: true,
                qty: units,
                batteryKwh: BATTERY_UNIT_KWH,
                unitPrice: battNetPrice,
                lineTotal: +(units * battNetPrice).toFixed(2),
                sortOrder: prev.length,
            }];
        });
        setBatteryKwhSel('');
    };

    // RSC-style auto-discount: compute Astern discount from the promos (phase- and
    // battery-aware) whenever they change. Manual create/edit only (showPromos).
    useEffect(() => {
        if (!showPromos || !setAsternDiscount || !promoTouchedRef.current) return;
        const is3p = phaseMode === '3phase';
        const hasBattery = (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0);
        const boxRemoved = promoBackupbox && !hasBattery;
        // Single source of truth (utils/rscPromos.js) — identical to RSC + createSalesForm,
        // so a promo toggle on create/edit can never freeze a stale amount. (user 2026-08-10)
        const astern = (boxRemoved ? backupboxRemoval(is3p) : 0) + (promoFirst640 ? suriaHomeBonus(boxRemoved, is3p) : 0);
        setAsternDiscount(astern);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPromos, promoBackupbox, promoFirst640, phaseMode, lineItems]);

    // Per-battery RM800 promo: fold into the battery line's unit price (16kWh only).
    useEffect(() => {
        if (!showPromos || !setLineItems || !promoTouchedRef.current) return;
        setLineItems(prev => {
            let changed = false;
            const next = prev.map(r => {
                if (r.isBattery && Number(r.batteryKwh) === 16 && Number(r.unitPrice) !== battNetPrice) {
                    changed = true;
                    return { ...r, unitPrice: battNetPrice, lineTotal: +(battNetPrice * (Number(r.qty) || 0)).toFixed(2) };
                }
                return r;
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPromos, promoPerBatt]);

    // Backup-Box add-on — REMOVAL MARKER ONLY (mirrors the RSC fix, user 2026-06-08):
    // the system base price ALREADY includes the backup box, so it must NEVER be
    // added as a priced line. The row exists ONLY when Backup-Box Removal is ticked
    // (no battery) — qty 0 / price-for-reference, documenting it was removed; the
    // −RM1,600/2,500 lives in the Astern-discount auto-fill. Box included normally
    // (removal unticked) → NO row. Battery present → no row (bundle includes it).
    useEffect(() => {
        if (!showPromos || !setLineItems || selectedCategory !== 'RESIDENTIAL' || !promoTouchedRef.current) return;
        const is3p = phaseMode === '3phase';
        const bbName = is3p ? 'Three Phase Backup-Box (Removed)' : 'Single Phase Backup-Box (Removed)';
        const bbPrice = is3p ? 2500 : 1600;   // reference price; qty 0 keeps it out of totals
        setLineItems(prev => {
            const list = prev || [];
            const hasBattery = list.some(li => li.isBattery && Number(li.qty) > 0);
            const others = list.filter(r => !r._isBackup);
            // Marker only when removal ticked AND no battery; otherwise no backup row.
            const needRow = promoBackupbox && !hasBattery;
            const next = needRow
                ? [...others, {
                    salesLineItemId: 0, itemName: bbName, category: 'Battery', isBattery: false,
                    qty: 0, unitPrice: bbPrice, lineTotal: 0,
                    _isBackup: true, sortOrder: others.length,
                }]
                : others;
            return JSON.stringify(list) === JSON.stringify(next) ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPromos, promoBackupbox, phaseMode, selectedCategory, lineItems]);

    // Battery present → the bundle includes the backup box, so any Backup-Box
    // "(Removed)" marker must disappear. Runs UNGATED (also for edited/reloaded sales
    // that never toggled a promo) — matches the bundle rule the user confirmed.
    useEffect(() => {
        if (!setLineItems) return;
        const hasBattery = (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0);
        if (!hasBattery) return;
        setLineItems(prev => {
            const next = (prev || []).filter(r => !r._isBackup && !/backup-box\s*\(removed\)/i.test(r.itemName || ''));
            return next.length === (prev || []).length ? prev : next;
        });
    }, [lineItems, setLineItems]);

    // ── RSC-style sizing / SEDA / CCC validation ─────────────────────────────
    // Same rules as the RSC calculator: read the matched row's inverter, check the
    // SEDA cap (1P 5kW / 3P 15kW) and the safety ratio, and require a CCC add-on
    // when the inverter is over-SEDA. A CCC line item (RM1,000) is detected by name.
    const CCC_RE = /\bccc\b|connection\s*confirmation/i;
    const cccPresent = (lineItems || []).some(li => CCC_RE.test(li.itemName || ''));
    const hasBatteryLine = (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0);
    const sizing = useMemo(() => {
        if (selectedCategory !== 'RESIDENTIAL') return null;
        return computeSizing({
            energy, phaseMode, inverterRow: residentialMatch,
            hasBattery: hasBatteryLine, cccPresent,
            lineItems, addonCatalog,
        });
    }, [selectedCategory, energy, phaseMode, residentialMatch, hasBatteryLine, cccPresent, lineItems, addonCatalog]);
    // Surface the verdict to the parent (for the create/edit submit gate).
    useEffect(() => {
        if (onSizingChange) onSizingChange(sizing);
    }, [sizing, onSizingChange]);
    // The CCC catalog add-on (RM1,000 SEDA registration).
    const cccAddon = useMemo(
        () => (addonCatalog || []).find(a => CCC_RE.test(a.itemName || '')),
        [addonCatalog]
    );
    const addCccRow = () => {
        if (!setLineItems) return;
        if ((lineItems || []).some(li => CCC_RE.test(li.itemName || ''))) return; // already present
        // Prefer the real catalog add-on — add it through the SAME parser the catalog
        // picker uses (base+rate aware, e.g. "1000+0" → RM1,000, not RM10,000).
        if (cccAddon) { addFromCatalog(cccAddon.addonId); return; }
        // Fallback only if no CCC row exists in the add-on catalog: synthetic RM1,000.
        setLineItems(prev => [...(prev || []), {
            salesLineItemId: 0, itemName: 'CCC (SEDA Registration)', category: 'CCC',
            isBattery: false, qty: 1, batteryKwh: null,
            priceBase: 0, priceRate: 0, unitPrice: 1000,
            lineTotal: 1000, sortOrder: (prev || []).length,
        }]);
    };

    // Agent Discount accepts NEGATIVES (= mark-up). A plain number input snaps a lone
    // "-" back to 0 (parseFloat("-") is NaN), so we keep a string mirror and only push
    // a valid number to the parent — letting the user type "-500" naturally.
    const [agentDiscStr, setAgentDiscStr] = useState(
        (agentDiscount === '' || agentDiscount == null) ? '' : String(agentDiscount)
    );
    const agentDiscSyncRef = useRef(agentDiscount);
    useEffect(() => {
        if (agentDiscount !== agentDiscSyncRef.current) {
            agentDiscSyncRef.current = agentDiscount;
            setAgentDiscStr((agentDiscount === '' || agentDiscount == null) ? '' : String(agentDiscount));
        }
    }, [agentDiscount]);
    const onAgentDiscInput = (raw) => {
        if (!/^-?\d*\.?\d*$/.test(raw)) return;   // digits, one dot, optional leading "-"
        setAgentDiscStr(raw);
        const n = (raw === '' || raw === '-' || raw === '.' || raw === '-.') ? 0 : parseFloat(raw);
        const val = isNaN(n) ? 0 : n;
        agentDiscSyncRef.current = val;
        if (setAgentDiscount) setAgentDiscount(val);
    };

    // Apply the suggested inverter upgrade: add each upgrade add-on (via the catalog
    // parser) + CCC if the target exceeds SEDA (mirrors RSC's upgrade pill).
    const applyInverterUpgrade = (upgrade) => {
        if (!setLineItems || !upgrade) return;
        (upgrade.addonIds || []).forEach(id => {
            const already = (lineItems || []).some(li => {
                const cat = (addonCatalog || []).find(a => String(a.addonId) === String(id));
                return cat && (li.itemName || '').trim() === (cat.itemName || '').trim();
            });
            if (!already) addFromCatalog(id);
        });
        if (upgrade.overSeda) addCccRow();
    };

    // Panel wattage is UI-only (not persisted). Default to 650W (current panel spec).
    const panelWattageOptions = ['550', '580', '600', '620', '640', '650'];
    const [panelWattage, setPanelWattage] = useState(initialPanelWattage ? String(initialPanelWattage) : '650');
    // RSC hand-off: when the seed prop arrives after mount, adopt it (without
    // recomputing energy — the kWp is already prefilled directly). Only fires when
    // the prop actually changes, so a user's manual wattage choice is never clobbered.
    useEffect(() => {
        if (initialPanelWattage) setPanelWattage(String(initialPanelWattage));
    }, [initialPanelWattage]);
    // Manual override: once the agent deliberately picks a wattage (e.g. 640W panels on a
    // commercial job while the list is 650), keep it — CSC lets you choose freely, so
    // create-sales should too. Until then, the wattage follows the ATAP list. (user 2026-08-10)
    const wattTouched = useRef(false);
    const panelWattageRef = useRef(panelWattage);
    useEffect(() => { panelWattageRef.current = panelWattage; }, [panelWattage]);
    // The panel wattage follows the ACTIVE ATAP pricing list (current or old snapshot):
    // it is kWp ÷ panel count of that list (current 650W / old 620W). Applied as soon as
    // the list wattage is known — even before panels are entered — so the label, the
    // "× kWp" helper and the auto-matched price all agree with the selected list. When
    // panels are already set we recompute kWp to stay on the list's grid. A MANUAL choice
    // (wattTouched) is never clobbered — the effect then keeps the chosen wattage and only
    // recomputes kWp from it. Price still matches by panel COUNT, so it's unaffected.
    useEffect(() => {
        const listW = Number(atapPanelWatt) || 0;
        if (listW <= 0) return;
        const w = wattTouched.current ? (parseInt(panelWattageRef.current) || listW) : listW;
        if (!wattTouched.current) {
            const ws = String(listW);
            setPanelWattage(prev => (prev === ws ? prev : ws));
        }
        if (Number(panelQty) > 0) {
            const e2 = Number((Number(panelQty) * w / 1000).toFixed(2));
            setEnergy(prev => (Number(prev) === e2 ? prev : e2));
        }
    }, [atapPanelWatt, panelQty]);
    const wattMultiplier = +(parseInt(panelWattage) / 1000).toFixed(2);

    const handleWattageChange = (val) => {
        if (!val) return;
        wattTouched.current = true;   // agent explicitly chose — stop the list from overriding it
        setPanelWattage(val);
        const mult = +(parseInt(val) / 1000).toFixed(2);
        if (panelQty > 0) {
            setEnergy(Number((panelQty * mult).toFixed(2)));
        }
    };

    // ── State + City reference data ────────────────────────────────
    const [cities, setCities] = useState([]);
    useEffect(() => {
        let cancelled = false;
        getAllCities()
            .then(res => { if (!cancelled) setCities(res.data || []); })
            .catch(err => console.error('Failed to load cities:', err));
        return () => { cancelled = true; };
    }, []);
    const stateOptions = useMemo(() => {
        const set = new Set();
        cities.forEach(c => { if (c.state) set.add(c.state); });
        return Array.from(set).sort();
    }, [cities]);
    const cityOptionsForState = useMemo(() => {
        if (!customerState) return [];
        return cities
            .filter(c => c.state === customerState)
            .map(c => c.cityName)
            .sort();
    }, [cities, customerState]);
    const customerFieldsLocked = disableCustomerFields && !isSuperAdmin;
    // Co-broke editability defaults to the main-agent lock unless explicitly overridden.
    const _coBrokeDisabled = disableCoBrokeFields ?? disableAgentFields;
    const _onCoBrokeGroupChange = handleCoBrokeGroupChange || handleAgentGroupChange;

    return (
        <>
            {/* Agent Information */}
            {sectionTitle('Agent Information')}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Agent Group')}
                    <TextField size="small" fullWidth disabled value={agentGroup || ""} InputProps={{ sx: inputSx }} />
                </Box>
                <Box>
                    {fieldLabel('Agent Name')}
                    {disableAgentFields ? (
                        <TextField size="small" fullWidth disabled value={agentName || ""} InputProps={{ sx: inputSx }} />
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select value={selectedAgentId || ''} onChange={e => setSelectedAgentId(e.target.value)} sx={inputSx}>
                                {[...agentNameOptions].sort(byName(o => o.label)).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <Box>
                    {fieldLabel('Co-broke Group')}
                    {_coBrokeDisabled ? (
                        <TextField size="small" fullWidth disabled value={coBrokeGroupName || ""} InputProps={{ sx: inputSx }} />
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select value={selectedCoBrokeGroupId || ''} onChange={_onCoBrokeGroupChange} sx={inputSx}>
                                <MenuItem value="">—</MenuItem>
                                {[...coBrokeGroupOptions].sort(byName(o => o.groupName)).map((o, i) => <MenuItem key={i} value={o.groupId}>{o.groupName}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                <Box>
                    {fieldLabel('Co-Agent (Offline)')}
                    {_coBrokeDisabled ? (
                        <TextField size="small" fullWidth disabled value={coBrokeAgentName || ""} InputProps={{ sx: inputSx }} />
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select disabled={!selectedCoBrokeGroupId} value={selectedCoBrokeAgentId || ''} onChange={e => setSelectedCoBrokeAgentId(e.target.value)} sx={inputSx}>
                                <MenuItem value="">—</MenuItem>
                                {[...coBrokeNameOptions].sort(byName(agentDisplay)).map(o => <MenuItem key={o.userId} value={o.userId}>{handleAgentName(o.firstName, o.lastName, o.alias)}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>

            {/* Referral picker — optional 3rd-party Referral-role user. Filtered to
                Referral-role only and excludes the Agent / CoAgent on the form. */}
            {setSelectedReferralId && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1 }}>
                    <Box>
                        {fieldLabel('Referral (Optional)')}
                        {/* TYPE TO FIND, not a scroll list. This was a plain Select, which was
                            right for the couple of dozen referrers that existed when it was
                            written and unusable the moment every customer can be one — 1,222
                            candidates on prod the day this changed. An Autocomplete filters as
                            you type, on name, alias, username or phone, so the length of the
                            list stops mattering.
                            (user 2026-11-01, on bulk-creating referrers: "any adjustment of
                             what?") */}
                        <Autocomplete
                            size="small"
                            disabled={disableReferralField}
                            options={[...referralOptions]
                                .sort(byName(agentDisplay))
                                .filter(o => String(o.userId) !== String(selectedAgentId)
                                          && String(o.userId) !== String(selectedCoBrokeAgentId))}
                            getOptionLabel={(o) => {
                                const composed = handleAgentName(o.firstName, o.lastName, o.alias);
                                return (composed && composed.trim()) || o.username || `User #${o.userId}`;
                            }}
                            /* Capped at 50 shown. Rendering a thousand rows on every keystroke
                               is what makes a long picker feel broken, and nobody scrolls past
                               fifty anyway — they type more. */
                            filterOptions={(opts, { inputValue }) => {
                                const q = (inputValue || '').trim().toLowerCase();
                                if (!q) return opts.slice(0, 50);
                                const digits = q.replace(/[^0-9]/g, '');
                                return opts.filter(o => {
                                    const name = [o.firstName, o.lastName, o.alias, o.username]
                                        .filter(Boolean).join(' ').toLowerCase();
                                    if (name.includes(q)) return true;
                                    const ph = String(o.phoneNumber || '').replace(/[^0-9]/g, '');
                                    return digits.length >= 3 && ph.includes(digits);
                                }).slice(0, 50);
                            }}
                            /* The phone is searchable AND shown beside the name: with a
                               thousand referrers, "which Tan?" is answered by the number. */
                            renderOption={(props, o) => {
                                const composed = handleAgentName(o.firstName, o.lastName, o.alias);
                                const display = (composed && composed.trim()) || o.username || `User #${o.userId}`;
                                return (
                                    <li {...props} key={o.userId}>
                                        <span>
                                            {display}
                                            {o.phoneNumber ? (
                                                <span style={{ color: '#94A3B8', fontSize: '0.78rem' }}> · {o.phoneNumber}</span>
                                            ) : null}
                                        </span>
                                    </li>
                                );
                            }}
                            isOptionEqualToValue={(o, v) => String(o.userId) === String(v && v.userId)}
                            value={referralOptions.find(o => String(o.userId) === String(selectedReferralId)) || null}
                            onChange={(e, v) => setSelectedReferralId(v ? v.userId : '')}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Type a name or phone…" sx={inputSx} />
                            )}
                            noOptionsText="No referrer by that name"
                        />
                        {/* Making a referrer used to mean leaving the sale, copying an invite
                            link, and the person filling in IC, address, a username and a
                            password — with a customer sitting there. Three fields, here,
                            instead. It sits BESIDE the picker now rather than inside it: the
                            picker became a search box, and an action does not belong among
                            search results. (user 2026-10-19) */}
                        {onCreateReferral && !disableReferralField && (
                            <Box
                                onClick={onCreateReferral}
                                sx={{ color: '#1A8CFF', fontWeight: 700, fontSize: '0.78rem',
                                      cursor: 'pointer', mt: 0.5, display: 'inline-block' }}
                            >
                                + Add new referrer
                            </Box>
                        )}
                        {/* The way an agent makes a referrer of their own. Admin can also
                            create one outright under Users; an agent cannot, and until now
                            had no visible route at all. */}
                        {currentUserId && <InviteReferrerButton userId={currentUserId} />}
                    </Box>

                    {/* Can this person actually be paid? A referral fee leaves by bank
                        transfer, built from the bank name, the account number and the
                        beneficiary name. A referrer missing any of them is discovered at
                        payout — by the accountant, weeks after the sale, with the money
                        already owed. Said here instead, while the person choosing them is
                        still looking at the screen.
                        (user 2026-10-13: "warning if refferal details not key in completely") */}
                    {selectedReferralId && (() => {
                        const ref = referralOptions.find(o => String(o.userId) === String(selectedReferralId));
                        if (!ref) return null;
                        // One flag from the server, not the bank details themselves —
                        // this list is readable by every agent.
                        if (ref.hasPayoutDetails !== false) return null;
                        return (
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <Alert severity="warning" icon={<WarningAmberRounded fontSize="small" />}
                                    sx={{ py: 0.25, '& .MuiAlert-message': { fontSize: '0.78rem' } }}>
                                    This referrer's bank details are incomplete — they cannot be
                                    paid until someone fills them in under Users. The sale can
                                    still be saved.
                                </Alert>
                            </Box>
                        );
                    })()}

                    {/* Who bears it. Shown only once a referrer is picked — on a sale with
                        no referral there is no fee and the question is noise.
                        READ-ONLY unless the viewer may set it: an agent sees who is paying,
                        which is worth knowing, but cannot move the cost off themselves or
                        onto themselves. Astern-borne is the default and means the fee never
                        touches the commission at all — it is an additional company cost on
                        top of it, not a slice out of it.
                        (user 2026-10-13: "by default u just tick the astern borned ...
                        this one they cant adjust, only we can do it in here") */}
                    {selectedReferralId && (
                        <Box sx={{ alignSelf: 'end', pb: 0.5 }}>
                            <FormControlLabel
                                sx={{ ml: 0 }}
                                control={<Checkbox size="small" checked={!!referralPaidByAstern}
                                    disabled={!canSetReferralPayer || !setReferralPaidByAstern}
                                    onChange={e => setReferralPaidByAstern && setReferralPaidByAstern(e.target.checked)} />}
                                label={<Box>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                        Referral fee borne by Astern
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                                        {referralPaidByAstern
                                            ? 'Paid by the company, on top of commission — no deduction from the agent'
                                            : 'Deducted from the selling agent\u2019s personal commission'}
                                        {canSetReferralPayer ? '' : ' \u00b7 admin only'}
                                    </Typography>
                                </Box>} />
                        </Box>
                    )}
                </Box>
            )}

            {/* Customer Information */}
            {sectionTitle('Customer Information')}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Customer Name', true)}
                    <TextField size="small" fullWidth required value={customerName || ''} onChange={e => setCustomerName(e.target.value)} disabled={disableCustomerFields && !isSuperAdmin} InputProps={{ sx: inputSx }} />
                </Box>
                <Box>
                    {fieldLabel('Phone Number', true)}
                    <TextField size="small" fullWidth required placeholder="012-345 6789" value={customerPhone || ''} onChange={e => setCustomerPhone(formatPhoneNumber(e.target.value))} disabled={disableCustomerFields && !isSuperAdmin} InputProps={{ sx: inputSx }} />
                </Box>
            </Box>
            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Email')}
                <TextField size="small" fullWidth type="email" value={customerEmail || ''} onChange={e => setCustomerEmail(e.target.value)} disabled={disableCustomerFields && !isSuperAdmin} InputProps={{ sx: inputSx }} />
            </Box>
            {/* IC and the birthday read out of it. Both read-only: the IC is typed in
                Form Details below (the field the PDFs use), and the birthday is the
                first six digits of it — never a separate answer that could disagree.
                Shown only where there is an IC, so a create form stays unchanged.
                (user 2026-10-21: "birthday help me check from system ic number") */}
            {customerIc ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                    <Box>
                        {fieldLabel('IC Number')}
                        <TextField size="small" fullWidth value={customerIc} disabled InputProps={{ sx: inputSx }} />
                    </Box>
                    <Box>
                        {fieldLabel('Birthday')}
                        <TextField size="small" fullWidth disabled
                            value={icBirthday(customerIc)
                                ? `${icBirthday(customerIc)}${icAge(customerIc) != null ? `  (${icAge(customerIc)} yrs)` : ''}`
                                : 'Not readable from this IC'}
                            InputProps={{ sx: inputSx }} />
                    </Box>
                </Box>
            ) : null}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('State', true)}
                    <FormControl size="small" fullWidth required>
                        <Select
                            value={customerState || ''}
                            onChange={e => {
                                if (setCustomerState) setCustomerState(e.target.value);
                                if (setCustomerCity) setCustomerCity('');
                            }}
                            disabled={customerFieldsLocked}
                            displayEmpty
                            sx={inputSx}
                        >
                            <MenuItem value=""><em>Select state…</em></MenuItem>
                            {stateOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    {fieldLabel('City', true)}
                    <FormControl size="small" fullWidth required>
                        <Select
                            value={customerCity || ''}
                            onChange={e => setCustomerCity && setCustomerCity(e.target.value)}
                            disabled={customerFieldsLocked || !customerState}
                            displayEmpty
                            sx={inputSx}
                        >
                            <MenuItem value=""><em>{customerState ? 'Select city…' : 'Select state first'}</em></MenuItem>
                            {cityOptionsForState.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Postcode')}
                <TextField size="small" fullWidth value={customerPostcode || ''}
                    onChange={e => setCustomerPostcode && setCustomerPostcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="e.g. 11900"
                    disabled={customerFieldsLocked}
                    InputProps={{ sx: inputSx }} />
            </Box>
            <Box sx={{ mb: 1 }}>
                {fieldLabel('Address (Street / No. / Jalan)', true)}
                <TextField size="small" fullWidth multiline rows={2} value={customerAddress || ''} onChange={e => setCustomerAddress(e.target.value)} disabled={customerFieldsLocked} InputProps={{ sx: inputSx }} placeholder="No. 12, Jalan Ampang…" />
            </Box>

            {/* ── Site Visit ──────────────────────────────────────────────────────
                Three facts about the ADDRESS, not the sale, so they live with it. The
                agent answers them on the day; Joyce would otherwise be ringing weeks
                later to ask. Gated and management approval also govern INSTALLATION
                day — the plates go to the guardhouse 1-2 days before the crew — which
                is why each box says which day it affects rather than relying on the
                heading. (user 2026-10-11, SOP v61 5.10 / 5.11) */}
            {(setSiteGatedGuarded || setMgmtApprovalNeeded) && (
                <Box sx={{ mb: 1.5, p: 1.25, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                    {fieldLabel('Site Visit')}
                    <FormControlLabel
                        sx={{ display: 'flex', ml: 0, mt: 0.25 }}
                        control={<Checkbox size="small" checked={!!siteGatedGuarded}
                            onChange={e => setSiteGatedGuarded && setSiteGatedGuarded(e.target.checked)}
                            disabled={customerFieldsLocked} />}
                        label={<Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Gated &amp; guarded</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                                Car plate numbers go to the guardhouse 1–2 days before installation
                            </Typography>
                        </Box>} />
                    <FormControlLabel
                        sx={{ display: 'flex', ml: 0 }}
                        control={<Checkbox size="small" checked={!!mgmtApprovalNeeded}
                            onChange={e => setMgmtApprovalNeeded && setMgmtApprovalNeeded(e.target.checked)}
                            disabled={customerFieldsLocked} />}
                        label={<Box>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Needs management approval</Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                                Customer gets the form from management; we fill in our details and apply
                            </Typography>
                        </Box>} />
                </Box>
            )}

            {/* Product Information */}
            {sectionTitle('Product Information')}
            {/* Category + Phase share one row (Phase = Residential only) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: selectedCategory === 'RESIDENTIAL' ? '1fr 1fr' : '1fr' }, gap: 1.5, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Product Category', true)}
                    <ToggleButtonGroup
                        value={selectedCategory}
                        exclusive
                        onChange={(e, val) => val && setSelectedCategory(val)}
                        disabled={disableProductFields}
                        fullWidth
                        size="small"
                        sx={{ '& .MuiToggleButton-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', py: 0.6, '&.Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)' } } } }}
                    >
                        <ToggleButton value="RESIDENTIAL">Residential</ToggleButton>
                        <ToggleButton value="COMMERCIAL">Commercial</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {selectedCategory === 'RESIDENTIAL' && (
                    <Box>
                        {fieldLabel('Phase')}
                        <ToggleButtonGroup
                            value={phaseMode}
                            exclusive
                            onChange={(e, val) => val && setPhaseMode(val)}
                            disabled={disableProductFields}
                            fullWidth
                            size="small"
                            sx={{ '& .MuiToggleButton-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', py: 0.6, '&.Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)' } } } }}
                        >
                            <ToggleButton value="single">Single Phase</ToggleButton>
                            <ToggleButton value="3phase">3 Phase</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}
                {/* Pricing-list snapshot: price the sale from a HISTORICAL list (e.g. the
                    640W list) instead of live. Only shown when snapshots exist. When set,
                    the whole quotation (price + panel-kWp mapping + frozen price) follows
                    the chosen list. Amber tint flags a non-live selection. */}
                {selectedCategory === 'RESIDENTIAL' && pricingSnapshots.length > 0 && (
                    <Box>
                        {fieldLabel('Pricing List')}
                        <FormControl size="small" fullWidth>
                            <Select
                                value={pricingSnapshotId ?? ''}
                                displayEmpty
                                onChange={e => setPricingSnapshotId(e.target.value === '' ? null : Number(e.target.value))}
                                disabled={disableProductFields}
                                sx={{ ...inputSx, bgcolor: pricingSnapshotId ? '#FEF3C7' : undefined }}
                            >
                                <MenuItem value="">Live (current)</MenuItem>
                                {pricingSnapshots.map(s => (
                                    <MenuItem key={s.historyId} value={s.historyId}>
                                        {s.name}{s.validFrom ? ` (${String(s.validFrom).slice(0, 10)}${s.validUntil ? ' → ' + String(s.validUntil).slice(0, 10) : '+'})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
            </Box>

            {/* Four fields on one line. The labels are kept to ONE line each and anything
                that needs explaining sits under the field instead: a label that wrapped
                pushed its own input half a row below the other three, which is what made
                the row look broken. alignItems start keeps the tops level whatever the
                helper text underneath does. (user 2026-10-04: "tally up the design") */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                gap: 1.25, mb: 1, alignItems: 'start',
            }}>
                <Box>
                    {fieldLabel('Panel Wattage')}
                    <FormControl size="small" fullWidth>
                        <Select
                            value={panelWattage}
                            onChange={e => handleWattageChange(e.target.value)}
                            // Defaults to the active ATAP pricing list's wattage but is
                            // EDITABLE — agents can pick e.g. 640W on a commercial job and it
                            // sticks (wattTouched guards the auto-sync). Price still matches
                            // by panel COUNT, so it's unaffected. (user 2026-08-10)
                            disabled={disableProductFields}
                            sx={inputSx}
                        >
                            {panelWattageOptions.map(w => (
                                <MenuItem key={w} value={w}>{w} W</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {atapPanelWatt && (
                        <Typography sx={helperSx}>From the pricing list · editable</Typography>
                    )}
                </Box>
                <Box>
                    {fieldLabel('Number of Panels', true)}
                    <TextField size="small" fullWidth type="number" value={panelQty || ''} disabled={disableProductFields}
                        onWheel={e => e.target.blur()}
                        onChange={e => { const qty = parseInt(e.target.value); setPanelQty(qty); setEnergy(Number((qty * wattMultiplier).toFixed(2))); }}
                        InputProps={{ sx: inputSx, inputProps: { min: 1, step: 1 } }} />
                </Box>
                <Box>
                    {fieldLabel('System Size (kWp)')}
                    <TextField size="small" fullWidth disabled value={energy || '0.00'} InputProps={{ sx: inputSx }} />
                    <Typography sx={helperSx}>{panelQty || 0} × {wattMultiplier} kWp</Typography>
                </Box>
                <Box>
                    {fieldLabel('Inverter (kWac)')}
                    <TextField size="small" fullWidth disabled
                        value={sizing?.inverterKw != null ? `${sizing.inverterKw} kW` : '—'}
                        InputProps={{ sx: inputSx }} />
                    <Typography sx={helperSx}>
                        {sizing?.upgraded ? `Upgraded from ${sizing.naturalKw} kW` : 'Final inverter size'}
                    </Typography>
                </Box>
            </Box>

            {/* RSC-style sizing verdict: inverter / ratio / SEDA-CCC. Only a VALID
                config (ratio safe + CCC when over-SEDA) can be created — the parent
                blocks submit on sizing.valid === false. */}
            {sizing && (
                <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, border: '1px solid', borderColor: sizing.valid ? '#BBF7D0' : '#FECACA', bgcolor: sizing.valid ? '#F0FDF4' : '#FEF2F2', fontFamily: PP }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.72rem' }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color: '#0F172A', fontWeight: 700 }}>
                            {sizing.phaseDigit}P {sizing.upgraded
                                ? <><span style={{ textDecoration: 'line-through', opacity: 0.5, marginRight: 4 }}>{sizing.naturalKw}kW</span>{sizing.inverterKw}kW</>
                                : `${sizing.inverterKw}kW`}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color: sizing.ratioOk ? '#16A34A' : '#D97706', fontWeight: 600 }}>
                            ratio {sizing.ratio.toFixed(2)} / {sizing.ratioCap}
                        </span>
                        <span style={{ color: '#64748B' }}>SEDA {sizing.sedaCap}kW</span>
                    </Box>
                    {!sizing.ratioOk && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#B45309' }}>{sizing.reason}</Typography>
                            {canEditAddons && (sizing.batteryResolves || sizing.upgrade) && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.4 }}>
                                    {sizing.batteryResolves && (
                                        <Button size="small" variant="text" onClick={() => addBattery(16)}
                                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#16A34A', bgcolor: '#DCFCE7', px: 1, py: 0.25, borderRadius: '6px', '&:hover': { bgcolor: '#BBF7D0' } }}>
                                            + Add 16kWh Battery
                                        </Button>
                                    )}
                                    {sizing.upgrade && (
                                        <Button size="small" variant="text" onClick={() => applyInverterUpgrade(sizing.upgrade)}
                                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#B45309', bgcolor: '#FEF3C7', px: 1, py: 0.25, borderRadius: '6px', '&:hover': { bgcolor: '#FDE68A' } }}>
                                            ⬆ Upgrade inverter {sizing.phaseDigit}P {sizing.upgrade.targetKw}kW{sizing.upgrade.overSeda ? ' & CCC' : ''} +RM{sizing.upgrade.cost.toLocaleString()}
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {sizing.upgrade?.overSeda && (
                                <Typography sx={{ fontSize: '0.62rem', color: '#B91C1C', mt: 0.4 }}>
                                    SEDA max {sizing.upgrade.sedaMax}kW — the upgrade also adds CCC (RM1,000) to register.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {sizing.ratioOk && sizing.cccRequired && (
                        <Box sx={{ mt: 0.5 }}>
                            {sizing.cccPresent ? (
                                <Typography sx={{ fontSize: '0.7rem', color: '#16A34A', fontWeight: 600 }}>
                                    ✓ CCC added — {sizing.inverterKw}kW SEDA-registered
                                </Typography>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#B91C1C', mb: 0.4 }}>{sizing.reason}</Typography>
                                    {canEditAddons && (
                                        <Button size="small" variant="text" onClick={addCccRow}
                                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#B91C1C', bgcolor: '#FEE2E2', px: 1, py: 0.25, borderRadius: '6px', '&:hover': { bgcolor: '#FECACA' } }}>
                                            + Add CCC RM1,000
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Wiring cable brand — under the product info, after the safety ratio (user 2026-07-24).
                Shows in the quotation's "Balance of System / all necessary wiring" line;
                "Any" prints no brand, a chosen brand prints brand + model. */}
            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Cable Brand')}
                <FormControl size="small" fullWidth>
                    <Select value={cableBrand || 'Any'} onChange={e => setCableBrand(e.target.value)} sx={inputSx}>
                        <MenuItem value="Any">Any (no brand shown)</MenuItem>
                        <MenuItem value="KUKA Cable">DC KUKA Cable — H1Z2Z2-K</MenuItem>
                        <MenuItem value="HELUKABEL">DC HELUKABEL SOLARFLEX®-X H1Z2Z2-K</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Payment Method', true)}
                <FormControl size="small" fullWidth>
                    <Select value={paymentMethod || ''} onChange={e => setPaymentMethod(e.target.value)} disabled={disableProductFields || disablePricingFields || availablePaymentMethods.length === 0} sx={inputSx}>
                        <MenuItem value="">Select Payment Method</MenuItem>
                        {availablePaymentMethods.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* BANK — shown once a credit-card term is chosen, required at 60 months.
                Below 60 every bank charges the same, so the field is informational there;
                at 60 the rate runs 7.20%–14.00% and decides BOTH the customer's price and
                the commission basis, so a sale saved without one would be priced on a rate
                no bank agreed to. (user 2026-10-29: "the % of 60m will follow the rate of
                the bank aso") */}
            {setCcBank && termForMethod(paymentMethod) > 0 && (() => {
                const term = termForMethod(paymentMethod);
                const required = term === 60;
                return (
                    <Box sx={{ mb: 1.5 }}>
                        {fieldLabel('Bank', required)}
                        <CcBankSelect
                            term={term} value={ccBank} method={ccBankMethod}
                            onChange={(b, m) => { setCcBank(b); setCcBankMethod && setCcBankMethod(m); }}
                            disabled={disablePricingFields} required={required}
                            error={required && !ccBank} sx={inputSx} />
                        <Typography sx={{ fontSize: '0.62rem', mt: 0.4, color: required && !ccBank ? '#EF4444' : '#64748B' }}>
                            {required && !ccBank
                                ? '60-month rates run 7.20%–14.00% by bank — the price and the commission basis both follow it.'
                                : term === 60
                                    ? 'Price and commission basis are converted at this bank’s rate.'
                                    : 'Same rate at every bank for this term — the bank only decides whether a payment link is possible.'}
                        </Typography>
                    </Box>
                );
            })()}

            {/* MIX (Cash + CC): enter the cash amount; the remainder finances on the chosen
                CC term (÷factor). Only the customer Total/Final change — commission stays on
                the cash Original − Astern. (user 2026-07-22) */}
            {isMixMethod(paymentMethod) && (
                <Box sx={{ mb: 1.5, p: 1.25, bgcolor: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 1.5 }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.6 }}>Mix — Cash + Credit Card</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Box>
                            {fieldLabel('Cash Amount (RM)')}
                            <TextField size="small" type="number" fullWidth value={mixCashAmount ?? ''}
                                onChange={e => setMixCashAmount(e.target.value)}
                                InputProps={{ sx: inputSx }} inputProps={{ min: 0 }} placeholder="e.g. 15000" />
                        </Box>
                        <Box>
                            {fieldLabel('CC Term (rest financed)')}
                            <FormControl size="small" fullWidth>
                                <Select value={mixTerm || 36} onChange={e => setMixTerm(Number(e.target.value))} sx={inputSx}>
                                    {MIX_TERM_OPTIONS.map(t => <MenuItem key={t} value={t}>{t} Months</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.64rem', color: '#0369A1', mt: 0.5 }}>
                        Cash paid upfront + the remainder ÷ CC factor. Commission stays on the cash Original − Astern.
                    </Typography>
                </Box>
            )}

            {/* Price Reference (display only) — the customer price WITHOUT the agent
                discount: cash + CC by formula, plus the pricing-version validity.
                Cash basis = matched package cash (or the entered method-price converted
                back to cash), so it's correct for any payment method. */}
            {(() => {
                const pm = String(paymentMethod || '').toLowerCase();
                const ccFactor = pm.includes('60') ? 0.9 : pm.includes('36') ? 0.925 : pm.includes('24') ? 0.935 : pm.includes('12') ? 0.955 : 1;
                const matchCash = residentialMatch
                    ? ((phaseMode === '3phase' ? (Number(residentialMatch.cash3p8k) || Number(residentialMatch.cashPrice)) : (Number(residentialMatch.cashPrice) || Number(residentialMatch.cash3p8k))) || 0)
                    : 0;
                // Pure SYSTEM original price for this panel count (the price-list row,
                // NOT net of discounts and NOT incl add-ons). Use the LIVE list for the
                // current/latest version (so it matches RSC + can't go stale when the
                // list is edited without re-snapshotting the history). Only a genuinely
                // BACK-DATED sale (deposit date in an older history version) uses that
                // version's frozen snapshot price.
                const isLatestRef = !activePricing || activePricing.historyId === latestHistoryId;
                const refCash = isLatestRef
                    ? (liveRefCash > 0 ? liveRefCash : (histRefCash > 0 ? histRefCash : (matchCash > 0 ? matchCash : Math.round((Number(originalPrice) || 0) * ccFactor))))
                    : (histRefCash > 0 ? histRefCash : (liveRefCash > 0 ? liveRefCash : (matchCash > 0 ? matchCash : Math.round((Number(originalPrice) || 0) * ccFactor))));
                if (refCash <= 0) return null;
                return (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 1.5, fontFamily: "'Poppins', sans-serif" }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#1A8CFF', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.4 }}>Price Reference - Original Price</Typography>
                        {/* Always show the full reference — cash + every CC term — so the
                            agent sees all options regardless of the selected method. */}
                        <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.74rem' }}>
                            <span style={{ minWidth: 70, color: '#1A8CFF' }}>Cash</span>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#0F172A' }}>RM {refCash.toLocaleString()}</span>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.7rem', mt: 0.25 }}>
                            <span style={{ minWidth: 70, color: '#1A8CFF', whiteSpace: 'nowrap' }}>Credit Card</span>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", color: '#0F172A' }}>12M RM {ccFromCashGF(refCash, 12).toLocaleString()} · 24M RM {ccFromCashGF(refCash, 24).toLocaleString()} · 36M RM {ccFromCashGF(refCash, 36).toLocaleString()} · 60M RM {(ccBank ? ccRoundUp10(refCash / factorFor(60, ccBank, ccBankMethod || null)) : (ccPriceRange(refCash, 60)?.max ?? ccFromCashGF(refCash, 60))).toLocaleString()}<span style={{ color: '#B45309', fontWeight: 700 }}>{ccBank ? ` ${ccBank}` : ' max'}</span></span>
                        </Box>
                        {activePricing && (() => {
                            // Latest list → "created date to present"; older lists keep their real range.
                            const isLatest = activePricing.historyId === latestHistoryId;
                            const fromDate = isLatest ? (activePricing.createdOn || activePricing.validFrom) : activePricing.validFrom;
                            const toLabel = (isLatest || !activePricing.validUntil) ? 'present' : fmtPriceDate(activePricing.validUntil);
                            return (
                                <Typography sx={{ fontSize: '0.66rem', color: '#1A8CFF', mt: 0.4, fontWeight: 600 }}>
                                    ℹ {activePricing.name || 'Price list'} · valid {fmtPriceDate(fromDate)} to {toLabel}
                                </Typography>
                            );
                        })()}
                    </Box>
                );
            })()}
            {salesPriceLens ? (
                // Cash-only model: ONE cash price box, anchored on the ATAP dated-list
                // reference (cashRef — the blue box's cash). The CC basis + commission
                // DERIVE from it (÷factor) and show read-only in the cards. Shows cashRef
                // by default; the editor can override. No CC typing, no mismatch warning.
                <Box sx={{ mb: 1.5 }}>
                    {fieldLabel('Original Cash Price (RM)', true)}
                    <TextField size="small" fullWidth type="number" disabled={!canEditPrice || !canEditCashPrice}
                        value={(ocp !== '' && ocp != null) ? ocp : (cashRef || '')}
                        onChange={e => setOcp && setOcp(parseFloat(e.target.value) || 0)}
                        placeholder="ATAP list cash"
                        helperText={!canEditCashPrice
                            ? 'Locked — only Accountant / Super Admin may change the cash price.'
                            : (cashRef > 0
                                ? `By ATAP pricing reference (RM ${cashRef.toLocaleString()}) — drives commission (÷ factor for Credit Card)`
                                : 'By ATAP pricing reference — drives commission (÷ factor for Credit Card)')}
                        InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                </Box>
            ) : (
                <Box sx={{ mb: 1.5 }}>
                    {fieldLabel('Original Price (RM · CASH ONLY)', true)}
                    <TextField size="small" fullWidth type="number" disabled={!canEditPrice}
                        value={originalPrice || ''} onChange={e => setOriginalPrice(parseFloat(e.target.value) || 0)}
                        placeholder={canEditPrice ? "Enter price" : "Auto-populated"}
                        InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                </Box>
            )}

            {/* RSC-style promos that auto-fill the Astern discount. On EDIT they are
                LOCKED (read-only) — they show what was applied at the sale but never
                recompute the frozen discount; edit the Astern Discount field directly. */}
            {showPromos && (selectedCategory === 'RESIDENTIAL') && !disablePricingFields && (() => {
                return (
                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    {fieldLabel(promosLocked ? 'Promotions (applied at sale — locked)' : 'Promotions (auto-fill Astern discount)')}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                            disabled={promosLocked || (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0)}
                            control={<Checkbox size="small" checked={!!promoBackupbox} onChange={e => { promoTouchedRef.current = true; setPromoBackupbox && setPromoBackupbox(e.target.checked); }} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>Backup-Box Removal (RM1,600 / RM2,500 — no battery only)</Typography>}
                        />
                        <FormControlLabel
                            disabled={promosLocked}
                            control={<Checkbox size="small" checked={!!promoFirst640} onChange={e => { promoTouchedRef.current = true; setPromoFirst640 && setPromoFirst640(e.target.checked); }} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>FIRST640 Promo (RM{suriaHomeBonus(false, phaseMode === '3phase').toLocaleString()} · RM{suriaHomeBonus(true, phaseMode === '3phase').toLocaleString()} if box removed)</Typography>}
                        />
                        <FormControlLabel
                            disabled={promosLocked || !(lineItems || []).some(li => li.isBattery && Number(li.qty) > 0)}
                            control={<Checkbox size="small" checked={!!promoPerBatt} onChange={e => { promoTouchedRef.current = true; setPromoPerBatt && setPromoPerBatt(e.target.checked); }} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>Per-Battery RM{PER_BATTERY_PROMO.toLocaleString()} off (16kWh — folds into battery price)</Typography>}
                        />
                    </Box>
                    {promosLocked && <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP, mt: 0.5 }}>Locked — change the Astern Discount field directly to adjust.</Typography>}
                </Box>
                );
            })()}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Astern Discount (RM · CASH ONLY)')}
                    <TextField size="small" fullWidth type="number" value={asternDiscount ?? ''} onWheel={e => e.target.blur()}
                        onChange={e => setAsternDiscount(parseFloat(e.target.value) || 0)} disabled={disablePricingFields}
                        placeholder="0.00" InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }}
                        helperText={showPromos ? 'Auto-filled from promotions above — editable' : undefined}
                        FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }} />
                </Box>
                <Box>
                    {fieldLabel('Agent Discount (RM · CASH ONLY)')}
                    <TextField size="small" fullWidth type="text" inputMode="decimal" value={agentDiscStr} onWheel={e => e.target.blur()}
                        onChange={e => onAgentDiscInput(e.target.value)} disabled={disablePricingFields}
                        placeholder="0.00 (use − for mark-up)" InputProps={{ sx: inputSx }}
                        helperText="Negative = mark-up"
                        FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }} />
                </Box>
            </Box>

            {/* Upline / Team discount sit with the other two discount buckets rather than at the
                foot of the form (user 2026-08-21). They were rendered inline by each caller,
                which put them below the price card and separated them from the discounts they
                belong with. Rendered here only when the caller passes the setters, so a screen
                that does not offer them is unaffected. */}
            {(setUplineDiscount || setTeamDiscount) && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Upline Discount (RM · CASH ONLY)')}
                    <TextField size="small" fullWidth type="number" value={uplineDiscount ?? ''} onWheel={e => e.target.blur()}
                        onChange={e => setUplineDiscount && setUplineDiscount(parseFloat(e.target.value) || 0)}
                        disabled={disablePricingFields || !setUplineDiscount}
                        placeholder="0.00" InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }}
                        helperText="Funded by the seller's upline(s)"
                        FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }} />
                </Box>
                <Box>
                    {fieldLabel('Team Discount (RM · CASH ONLY)')}
                    <TextField size="small" fullWidth type="number" value={teamDiscount ?? ''} onWheel={e => e.target.blur()}
                        onChange={e => setTeamDiscount && setTeamDiscount(parseFloat(e.target.value) || 0)}
                        disabled={disablePricingFields || !setTeamDiscount}
                        placeholder="0.00" InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }}
                        helperText="Funded by the leader(s)"
                        FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }} />
                </Box>
            </Box>
            )}

            {/* Deduct SST (commercial) — the discount you TYPE (Astern + Agent) deducts the
                SST first: SST charged = max(0, SST − Astern − Agent); anything beyond the SST
                comes off the price. Tick alone (no discount) does nothing. Commission is
                unchanged — Astern still lowers the Original−Astern base, Agent doesn't.
                (user 2026-08-10) */}
            {selectedCategory === 'COMMERCIAL' && !disablePricingFields && (
                <Box sx={{ mb: 1.5, p: 1.25, backgroundColor: '#EFF6FF', borderRadius: '8px', border: '1px solid #BFDBFE' }}>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={!!deductSst} onChange={e => setDeductSst && setDeductSst(e.target.checked)} />}
                        label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem', fontWeight: 600, color: '#1E3A8A' }}>
                            Deduct SST — the Astern + Agent discount cancels SST first
                        </Typography>} />
                    <Typography sx={{ fontSize: '0.68rem', color: '#2563EB', fontFamily: PP, mt: 0.25, lineHeight: 1.35 }}>
                        {(() => {
                            const _disc = (Number(asternDiscount) || 0) + (Number(agentDiscount) || 0);
                            if (!deductSst) return 'When ticked, the discount you type is applied to the SST first (waives it up to the discount); any excess comes off the price. Tick alone with no discount does nothing.';
                            if (_disc <= 0) return 'Type an Astern / Agent discount above — that amount deducts the SST. With no discount, SST is still charged in full.';
                            return `Your RM ${_disc.toLocaleString()} discount deducts the SST first; any amount beyond the SST comes off the price. Commission is unchanged.`;
                        })()}
                    </Typography>
                </Box>
            )}

            {/* Discount Funded By — only show when agent discount > 0 and in create mode */}
            {agentDiscount > 0 && setDiscountFundedBy && (
                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    {fieldLabel('Discount Funded By')}
                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                        <Select value={discountFundedBy || 'Own'} onChange={e => {
                            setDiscountFundedBy(e.target.value);
                            if (e.target.value === 'Own') { setAgentDiscountContribution(agentDiscount); setUplineDiscountContribution(0); }
                            else if (e.target.value === 'CompanyUpline') { setAgentDiscountContribution(0); setUplineDiscountContribution(agentDiscount); }
                        }} sx={inputSx}>
                            <MenuItem value="Own">My Own</MenuItem>
                            <MenuItem value="CompanyUpline">Company & Upline</MenuItem>
                            <MenuItem value="Split">Split</MenuItem>
                        </Select>
                    </FormControl>
                    {discountFundedBy === 'Split' && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <Box>
                                {fieldLabel('My Contribution (RM)')}
                                <TextField size="small" fullWidth type="number" value={agentDiscountContribution ?? ''} onWheel={e => e.target.blur()}
                                    onChange={e => { const v = parseFloat(e.target.value) || 0; setAgentDiscountContribution(v); setUplineDiscountContribution(Math.max(0, (agentDiscount || 0) - v)); }}
                                    InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                            </Box>
                            <Box>
                                {fieldLabel('Company & Upline Contribution (RM)')}
                                <TextField size="small" fullWidth type="number" value={uplineDiscountContribution ?? ''} onWheel={e => e.target.blur()}
                                    onChange={e => { const v = parseFloat(e.target.value) || 0; setUplineDiscountContribution(v); setAgentDiscountContribution(Math.max(0, (agentDiscount || 0) - v)); }}
                                    InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                            </Box>
                        </Box>
                    )}
                    {discountFundedBy === 'CompanyUpline' && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", mt: 0.5 }}>
                            Full agent discount of RM {(agentDiscount || 0).toFixed(2)} will be recorded as funded by company & upline
                        </Typography>
                    )}
                </Box>
            )}

            {/* Add-on Items Table */}
            <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    {fieldLabel('Add-on Items')}
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP }}>
                        Total: <strong style={{ color: '#0F172A' }}>RM {addonTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</strong>
                    </Typography>
                </Box>

                {/* Table */}
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', mb: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 80px 28px', gap: 0, bgcolor: '#F8FAFC', px: 1, py: 0.5 }}>
                        {['Item', 'Qty', 'Unit Price', 'Total', ''].map((h, i) => (
                            <Typography key={i} sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: PP, textAlign: i >= 2 ? 'right' : 'left', pr: i === 3 ? 1 : 0 }}>
                                {h}
                            </Typography>
                        ))}
                    </Box>

                    {/* Rows */}
                    {(lineItems || []).length === 0 ? (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', fontFamily: PP, fontStyle: 'italic', textAlign: 'center', py: 1.5 }}>
                            No add-ons yet — pick from catalog below
                        </Typography>
                    ) : (lineItems || []).map((row, idx) => (
                        <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 80px 28px', gap: 0, alignItems: 'center', px: 1, py: 0.4, borderTop: '1px solid #F1F5F9', bgcolor: row.isBattery ? '#F0FDF4' : 'transparent' }}>
                            {/* Item name (+ base+rate structure caption for qty-priced add-ons) */}
                            <Box sx={{ minWidth: 0, pr: 0.5 }}>
                                <Typography sx={{ fontSize: '0.78rem', color: '#0F172A', fontFamily: PP }} noWrap title={row.itemName}>
                                    {row.itemName}
                                    {row.isBattery && <span style={{ marginLeft: 4, fontSize: '0.6rem', color: '#16A34A', fontWeight: 700 }}>BATT</span>}
                                </Typography>
                                {Number(row.priceRate) > 0 && (
                                    canEditAddons ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                            <span style={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP, whiteSpace: 'nowrap' }}>Base RM</span>
                                            <TextField
                                                size="small" type="number" value={row.priceBase ?? 0}
                                                onWheel={e => e.target.blur()}
                                                onChange={e => updateRow(idx, { priceBase: parseFloat(e.target.value) || 0 })}
                                                sx={{ width: 70, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.68rem', py: '2px', px: '5px' }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                            <span style={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP, whiteSpace: 'nowrap' }}>+ RM {Number(row.priceRate).toLocaleString()}/unit</span>
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP, lineHeight: 1.2 }} noWrap>
                                            RM {Number(row.priceBase || 0).toLocaleString()} base + RM {Number(row.priceRate).toLocaleString()}/unit
                                        </Typography>
                                    )
                                )}
                            </Box>
                            {/* Qty */}
                            {canEditAddons ? (
                                <TextField
                                    size="small" type="number" value={row.qty ?? 1}
                                    onWheel={e => e.target.blur()}
                                    // Allow qty 0 (e.g. backup-box removed) — a 0-qty row stays
                                    // visible to show the removed state, contributes 0 to the total,
                                    // and is hidden in the generated PDF.
                                    onChange={e => updateRow(idx, { qty: Math.max(0, parseInt(e.target.value) || 0) })}
                                    sx={{ '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.78rem', py: '3px', px: '6px', textAlign: 'center' }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            ) : (
                                <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontFamily: PP, textAlign: 'center' }}>{row.qty}</Typography>
                            )}
                            {/* Unit Price (= per-unit RATE for base+rate rows) */}
                            {canEditAddons ? (
                                <TextField
                                    size="small" type="number" value={row.unitPrice ?? 0}
                                    onWheel={e => e.target.blur()}
                                    onChange={e => updateRow(idx, Number(row.priceRate) > 0
                                        ? { priceRate: parseFloat(e.target.value) || 0 }
                                        : { unitPrice: parseFloat(e.target.value) || 0 })}
                                    sx={{ '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.78rem', py: '3px', px: '6px', textAlign: 'right' }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                            ) : (
                                <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontFamily: PP, textAlign: 'right', pr: 0.5 }}>
                                    {Number(row.unitPrice || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                                </Typography>
                            )}
                            {/* Line Total */}
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0F172A', fontFamily: PP, textAlign: 'right', pr: 0.5 }}>
                                {Number(row.lineTotal || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                            </Typography>
                            {/* Remove */}
                            {canEditAddons ? (
                                <IconButton size="small" onClick={() => removeRow(idx)} sx={{ p: 0.25, color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}>
                                    <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                            ) : <span />}
                        </Box>
                    ))}

                    {/* Footer total */}
                    {(lineItems || []).length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', gap: 1, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP }}>Add-on Total</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, minWidth: 80, textAlign: 'right' }}>
                                RM {addonTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Catalog picker + Battery picker — only shown to editors */}
                {canEditAddons && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {addonCatalog.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                                <Select
                                    displayEmpty
                                    value=""
                                    onChange={e => { addFromCatalog(e.target.value); }}
                                    sx={{ fontFamily: PP, fontSize: '0.8rem' }}
                                    renderValue={() => <em style={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8' }}>+ Add from catalog…</em>}
                                >
                                    <MenuItem value="" disabled><em>Select an add-on</em></MenuItem>
                                    {addonCatalog.map(a => (
                                        <MenuItem key={a.addonId} value={a.addonId} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                            {a.itemName}
                                            {a.price ? <span style={{ marginLeft: 8, color: '#64748B', fontSize: '0.72rem' }}>RM {a.price}</span> : null}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {/* Battery picker — choose kWh (16..160), adds an ATAP-priced battery line */}
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select
                                displayEmpty
                                value={batteryKwhSel}
                                onChange={e => setBatteryKwhSel(e.target.value)}
                                sx={{ fontFamily: PP, fontSize: '0.8rem' }}
                                renderValue={(v) => v
                                    ? <span style={{ fontFamily: PP, fontSize: '0.8rem' }}>{v} kWh battery</span>
                                    : <em style={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8' }}>+ Battery (kWh)…</em>}
                            >
                                <MenuItem value="" disabled><em>Select battery kWh</em></MenuItem>
                                {batteryKwhOptions.map(k => (
                                    <MenuItem key={k} value={k} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                        {k} kWh <span style={{ marginLeft: 8, color: '#64748B', fontSize: '0.72rem' }}>RM {(Math.ceil(k / BATTERY_UNIT_KWH) * BATTERY_UNIT_PRICE).toLocaleString()}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!batteryKwhSel}
                            onClick={() => addBattery(batteryKwhSel)}
                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px', borderColor: '#16A34A', color: '#16A34A', whiteSpace: 'nowrap' }}
                        >
                            Add Battery
                        </Button>
                    </Box>
                )}

                {/* Add-on Price: derived (greyed) when the table has items; otherwise
                    an editable lump-sum so old sales keep their existing amount. */}
                <Box sx={{ mt: 1 }}>
                    {fieldLabel('Add-on Price (RM · CASH ONLY)')}
                    {hasLineItems ? (
                        <TextField
                            size="small" fullWidth type="number"
                            value={addonTotal.toFixed(2)}
                            disabled
                            InputProps={{ sx: { ...inputSx, bgcolor: '#F8FAFC', color: '#64748B' }, inputProps: { step: 0.01, min: 0 } }}
                            helperText="Auto-computed from add-on table above"
                            FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }}
                        />
                    ) : (
                        <TextField
                            size="small" fullWidth type="number"
                            value={addOnPrice ?? ''}
                            onChange={e => setAddOnPrice && setAddOnPrice(e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                            disabled={disablePricingFields}
                            onWheel={e => e.target.blur()}
                            InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }}
                            helperText="Lump-sum add-on amount (no add-on line items). Change only if needed."
                            FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Deposit Payment Date')}
                <DatePicker format="DD/MM/YYYY" disabled={disableDepositDate}
                    value={depositOn ? dayjs(convertDateTimeToInputDateString(depositOn)) : null}
                    onChange={(val) => setDepositOn(val ? val.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: { sx: inputSx } } }} />
            </Box>

            {/* Payment-method pricing (the SAVED / quotation / commission basis). Uses
                the shared methodBreakdown so the create form, the edit form, the
                quotation PDF and the commission all tally exactly. Cash fields (Section
                1) above are the base; this converts system & add-on each ÷ factor
                (round-up RM10), discounts rounded, Final = Original − discounts. */}
            {(Number(originalPrice) > 0 || Number(ocp) > 0 || Number(cashRef) > 0) && (() => {
                const isMix = isMixMethod(paymentMethod);
                const term = isMix ? (Number(mixTerm) || 36) : termForMethod(paymentMethod);   // 0 = cash, else CC / MIX term
                const isCC = term > 0;
                // cash -> CC on THIS card's bank. ccFromCash would use the flat factor and
                // disagree with the breakdown beside it on any 60-month sale.
                const ccPriceAt = (cash, t) => {
                    const c = Number(cash) || 0;
                    if (c <= 0 || !t) return 0;
                    return ccRoundUp10(c / factorFor(t, ccBank, ccBankMethod || null));
                };
                // CASH ANCHOR: an editor override (ocp) wins; else the ATAP dated-list
                // reference (cashRef — unpolluted, exact); else the entered price (create
                // mode). NEVER a ÷factor back-derive of a stored value. Cards show the cash
                // basis and — for a CC term — the CC (÷factor) basis side by side; the CC
                // card's Final is the commission (cash 19,450 → 21,263 on 36M).
                const cashBase = salesPriceLens
                    ? (Number(ocp) > 0 ? Number(ocp) : (Number(cashRef) > 0 ? Number(cashRef) : (Number(originalPrice) || 0)))
                    : (Number(originalPrice) || 0);   // create mode: the entered cash price
                const cashBd = methodBreakdown({ originalPrice: cashBase, asternDiscount, agentDiscount, uplineDiscount, teamDiscount, addOnPrice }, 0);
                // Add-on CC conversion (user 2026-07-08): NEW sales (create mode, or deposit ≥ the
                // CC add-on conversion (user 2026-07-10): any CC sale WITH line items converts the
                // add-on so the CC Total tallies with RSC (whole cash ÷ factor). ALWAYS convert from
                // the CASH line-item sum (fixed) — never a stored/previous CC value — so re-editing
                // re-derives the same number and never drifts. No date gate: an OLDER sale converts
                // the moment it's opened/edited (applies on edit), but sales you never touch keep
                // their stored values → NO mass backfill. Lump-add-on sales (no line items) stay
                // FLAT. Commission (Original − Astern) is unaffected either way.
                const _hasLI = (lineItems || []).length > 0;
                const _cashAddon = _hasLI ? (Number(addonTotal) || 0) : (Number(addOnPrice) || 0);
                // Deposit-date gate (restored 2026-07-22): the add-on ÷factor conversion applies
                // ONLY to sales dated on/after the CC add-on cutoff (2026-07-08). An OLDER sale
                // (e.g. a cash sale created before then, now switched to a CC term) keeps the
                // add-on FLAT — the same value the cash-reference card shows — so it is NOT
                // double-converted (e.g. #1523: 341 → 390). New sales (create mode / no deposit
                // yet, or deposit ≥ cutoff) still convert for RSC parity.
                const _depTs = depositOn ? Date.parse(depositOn) : NaN;
                const _depConverts = isNaN(_depTs) || _depTs >= Date.parse('2026-07-08T00:00:00');
                const _convertAddon = isCC && !isMix && _hasLI && _depConverts && (!salesPriceLens || saleTouched);
                // MIX: split the WHOLE cash total (final + add-on) by the cash amount; the
                // remainder ÷factor. Commission (Original − Astern) stays cash-basis inside
                // mixBreakdown. Pure CC uses methodBreakdown as before. (user 2026-07-22)
                const ccBd = isMix
                    ? mixBreakdown({ originalPrice: cashBase, asternDiscount, agentDiscount, uplineDiscount, teamDiscount, addOnPrice: _cashAddon }, term, Number(mixCashAmount) || 0)
                    : (isCC ? methodBreakdown({ originalPrice: cashBase, asternDiscount, agentDiscount, uplineDiscount, teamDiscount, addOnPrice: _convertAddon ? _cashAddon : addOnPrice }, term, { bank: ccBank, bankMethod: ccBankMethod || null }) : null);
                // Old / lump CC sales: keep the add-on flat (don't ÷factor it). New sales keep
                // methodBreakdown's whole-total conversion (add-on = residual → rows sum to total).
                // MIX already handled its own add-on inside mixBreakdown — leave it.
                if (ccBd && !isMix && !_convertAddon) { const _flatAddon = Number(addOnPrice) || 0; ccBd.addon = _flatAddon; ccBd.total = ccBd.final + _flatAddon; }
                // Manual override (CC AND MIX): keep the editor's typed Original/Astern/Agent/Final.
                // CC add-on converts from the cash line items (new sales); MIX keeps its financed
                // add-on from mixBreakdown (the override adjusts the SYSTEM price only). Total =
                // Final + add-on. (user 2026-07-22: MIX card is editable, same as CC.)
                if (ccBd && ccOverride) {
                    ccBd.original = Number(ccOverride.original) || 0;
                    ccBd.astern   = Number(ccOverride.astern) || 0;
                    // Agent, like Final, falls through to the CASH-derived figure when the key is
                    // unset — the edit page releases it on a cash-side discount edit so the change
                    // actually shows. Agent/Upline/Team only divide-by-factor the live cash inputs,
                    // so they are right even when there is no cash anchor for Original. (2026-08-31)
                    if (ccOverride.agent !== '' && ccOverride.agent != null) ccBd.agent = Number(ccOverride.agent) || 0;
                    // Upline / Team mirror the override when one was typed, else keep the
                    // cash-derived figure — and BOTH must come off the auto-derived Final.
                    // The old fallback stopped at Agent, so a sale carrying an upline/team
                    // discount showed a Final that was too HIGH by those two buckets while the
                    // save (editSalePage) subtracted them — card and stored price disagreed.
                    // (fix 2026-08-31)
                    if (ccOverride.upline !== '' && ccOverride.upline != null) ccBd.upline = Number(ccOverride.upline) || 0;
                    if (ccOverride.team !== '' && ccOverride.team != null) ccBd.team = Number(ccOverride.team) || 0;
                    ccBd.final    = (ccOverride.final !== '' && ccOverride.final != null)
                        ? (Number(ccOverride.final) || 0)
                        : Math.max(0, ccBd.original - ccBd.astern - ccBd.agent
                            - (Number(ccBd.upline) || 0) - (Number(ccBd.team) || 0));
                    // Add-on is editable + financed (user 2026-07-28). Priority:
                    //   1) an explicit typed override (ccOverride.addon), else
                    //   2) MIX → mixBreakdown's ÷factor financed add-on — the amount the customer
                    //      actually pays (financed on the CC portion). Captured here before we
                    //      overwrite, since the override block only re-derives the SYSTEM price.
                    //   3) CC → converted line-item add-on for new sales, else flat (date-gated).
                    // Commission (Original − Astern) is unaffected — add-on never earns commission,
                    // so an editable add-on only moves the customer Total / QuotationTotal.
                    const _mixFinancedAddon = isMix ? (Number(ccBd.addon) || 0) : 0;
                    // methodBreakdown's total — the whole cash total rounded up to RM10 ONCE.
                    // Captured before we overwrite it below.
                    const _mbTotal = Number(ccBd.total) || 0;
                    if (ccOverride.addon !== '' && ccOverride.addon != null) {
                        ccBd.addon = Number(ccOverride.addon) || 0;
                        ccBd.total = ccBd.final + ccBd.addon;
                    } else if (isMix) {
                        ccBd.addon = _mixFinancedAddon;
                        ccBd.total = ccBd.final + ccBd.addon;
                    } else if (ccOverride._seed && _convertAddon && _mbTotal > ccBd.final) {
                        // SINGLE round-up (user 2026-08-31: "single round up is correct").
                        // An UNTOUCHED sale kept its own round-up on the add-on AND another on the
                        // system price, so the customer total came out up to RM10 above the rule
                        // methodBreakdown documents — round the WHOLE cash total up once, add-on =
                        // residual. Worked example: system 27,952/0.925 = 30,218.38 -> 30,220 and
                        // add-on 7,650/0.925 = 8,270.27 -> 8,280 summed to 38,500, where the single
                        // round 35,602/0.925 = 38,488.65 -> 38,490. Create-sales always rounded
                        // once, so opening a sale on the edit page silently added RM10 on save.
                        ccBd.total = _mbTotal;
                        ccBd.addon = _mbTotal - ccBd.final;
                    } else {
                        // A TYPED override is the editor driving the price — respect their Final
                        // and finance the add-on on its own.
                        // Same bank factor as the row above it — a displayed add-on converted at the
                        // flat rate beside a total converted at the bank's would not sum.
                        ccBd.addon = _convertAddon ? ccPriceAt(_cashAddon, term) : (Number(addOnPrice) || 0);
                        ccBd.total = ccBd.final + ccBd.addon;
                    }
                }
                // Commercial quotations carry 6% SST on the 20% portion (= total × 0.012). The
                // customer pays the SST-inclusive grand total, so the "Total Quotation" line + the
                // progress-bar denominator + the persisted QuotationTotal must include it.
                // Residential = no SST. (user 2026-07-23)
                const isCommercial = selectedCategory === 'COMMERCIAL';
                // SST via the single canonical helper (utils/sst.js) — whole-ringgit, same
                // round-off as every quotation + commission path. (user 2026-07-28)
                const sstOf = (t) => isCommercial ? sstOn(t) : 0;
                const withSst = (t) => (Number(t) || 0) + sstOf(t);
                // "Deduct SST" (commercial): the typed Astern + Agent discount DEDUCTS the SST
                // first — SST payable = max(0, fullSST − discount); any discount beyond the SST
                // comes off the price. The customer keeps the full system price otherwise, and
                // commission is unchanged (it still follows Original−Astern via bd). Tick alone
                // with no discount = no effect (fullSST still charged). (user 2026-08-10)
                const dedOn = isCommercial && !!deductSst;
                const sstFullOf = (bd) => sstOn((Number(bd.original) || 0) + (Number(bd.addon) || 0));
                const discOf = (bd) => (Number(bd.astern) || 0) + (Number(bd.agent) || 0);
                const sstPayableOf = (bd) => dedOn ? Math.max(0, sstFullOf(bd) - discOf(bd)) : sstOf(bd.total);
                const custTotalOf = (bd) => {
                    if (!dedOn) return withSst(bd.total);
                    const excess = Math.max(0, discOf(bd) - sstFullOf(bd));   // discount beyond SST reduces price
                    return (Number(bd.original) || 0) - excess + (Number(bd.addon) || 0) + sstPayableOf(bd);
                };
                // LIVE Total Quotation = the customer-paying total (the "pays" card), incl SST for
                // commercial. Reported up for the payment bar.
                _qTotalRef.current = custTotalOf((isCC && ccBd) ? ccBd : cashBd);
                // opts.comm = feeds commission (green dot); opts.muted = not (grey);
                // opts.pays = the headline Final.
                const row = (label, val, opts = {}) => {
                    const v = Number(val) || 0;
                    const isMarkup = opts.neg && v < 0;   // a negative discount = mark-up
                    const valColor = opts.muted ? '#94A3B8'
                        : (isMarkup ? '#16A34A' : (opts.neg ? '#B91C1C' : (opts.strong ? '#16A34A' : '#0F172A')));
                    const labelColor = opts.muted ? '#94A3B8' : (opts.strong ? '#0369A1' : '#475569');
                    return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: opts.strong ? '0.82rem' : '0.74rem', fontWeight: opts.strong ? 700 : 500, mt: opts.strong ? 0.4 : 0.15, pt: opts.strong ? 0.4 : 0, borderTop: opts.strong ? '1px solid #BFDBFE' : 'none' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: labelColor }}>
                            {opts.comm && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16A34A', display: 'inline-block', flexShrink: 0 }} />}
                            {label}{isMarkup ? ' (mark-up)' : ''}
                            {opts.pays && <span style={{ color: '#15803D', fontWeight: 800, fontSize: '0.62rem' }}>◄ pays</span>}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color: valColor }}>{isMarkup ? '+ ' : (opts.neg ? '− ' : '')}RM {Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </Box>
                    );
                };
                // `saved` = the card that PAYS (CC card for a CC term, cash card for
                // Cash/Mix). Its 4 commission rows get a green dot + a COMMISSION chip;
                // Add-on / Total muted on every card (never earn commission).
                const panel = (title, bd, saved, sfx = '') => (
                    <Box sx={{ flex: 1, p: 1.25, bgcolor: saved ? '#EFF6FF' : '#F8FAFC', border: '1px solid', borderColor: saved ? '#86C7BC' : '#E2E8F0', borderRadius: 2, fontFamily: PP }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: saved ? '#1A8CFF' : '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                {title}{saved ? '' : ' · reference (by ATAP pricing)'}
                            </Typography>
                            {saved && (
                                <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.7, py: 0.15, bgcolor: '#DCFCE7', color: '#15803D', borderRadius: 1, fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.3px' }}>
                                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16A34A' }} /> COMMISSION
                                </Box>
                            )}
                        </Box>
                        {row(`Original Price${sfx}`, bd.original, { comm: saved })}
                        {row(`Astern Discount${sfx}`, bd.astern, { neg: true, comm: saved })}
                        {row(`Agent Discount${sfx}`, bd.agent, { neg: true, comm: saved })}
                        {/* Upline / Team only appear when they carry a value. They're rare, and a
                            permanent "− RM 0" pair on every quotation is noise — but when someone
                            HAS funded a discount, the breakdown has to show it or Final Price looks
                            wrong by that amount. (user 2026-08-28) */}
                        {!!bd.upline && row(`Upline Discount${sfx}`, bd.upline, { neg: true, comm: saved })}
                        {!!bd.team && row(`Team Discount${sfx}`, bd.team, { neg: true, comm: saved })}
                        {row(`Final Price${sfx}`, bd.final, { strong: true, comm: saved, pays: saved })}
                        {row('Add-on', bd.addon, { muted: true })}
                        {isCommercial && row(dedOn ? `SST 6% (${discOf(bd) > 0 ? `− RM ${discOf(bd).toLocaleString()} deducted` : 'no discount yet'})` : 'SST 6% (on 20%)', sstPayableOf(bd), { muted: true })}
                        {row(isCommercial ? (dedOn ? 'Total Quotation (SST deducted)' : 'Total Quotation (with SST)') : 'Total Quotation', custTotalOf(bd), { strong: true, muted: true })}
                    </Box>
                );
                // Item 5 — editable CC card (Super Admin / Accountant). Four inputs, all
                // free-form; typing any of them seeds the override from the current derived
                // values and marks it active. Add-on / Total stay computed.
                const ccEditVal = (k) => (ccOverride && ccOverride[k] !== undefined && ccOverride[k] !== null) ? ccOverride[k] : (ccBd ? ccBd[k] : 0);
                const onCcEdit = (k, raw) => {
                    if (!setCcOverride) return;
                    // Upline/Team belong in the base too: without them, typing into ANY cc row
                    // rebuilt the override without those two keys, so they silently fell back
                    // to the cash re-derivation while every other row stayed pinned.
                    const base = ccOverride || { original: ccBd.original, astern: ccBd.astern, agent: ccBd.agent,
                        ...(ccBd.upline ? { upline: ccBd.upline } : {}),
                        ...(ccBd.team ? { team: ccBd.team } : {}),
                        final: ccBd.final };
                    setCcOverride({ ...base, _seed: false, [k]: raw === '' ? '' : (parseFloat(raw) || 0) });
                };
                const ccEditRow = (label, k, opts = {}) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: opts.strong ? '0.82rem' : '0.74rem', fontWeight: opts.strong ? 700 : 500, color: opts.strong ? '#0369A1' : '#475569' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#16A34A', display: 'inline-block', flexShrink: 0 }} />
                            {label}
                            {opts.pays && <span style={{ color: '#15803D', fontWeight: 800, fontSize: '0.62rem' }}> ◄ pays</span>}
                        </span>
                        <TextField size="small" type="number" value={ccEditVal(k)} onChange={e => onCcEdit(k, e.target.value)} onWheel={e => e.target.blur()}
                            sx={{ width: 128 }} InputProps={{ sx: { fontFamily: "'JetBrains Mono',monospace", fontSize: '0.78rem' }, inputProps: { step: 0.01 } }} />
                    </Box>
                );
                // Add-on is editable too — pre-filled with the financed (÷factor) value the customer
                // actually pays. No green dot / muted colour: add-on never earns commission, so editing
                // it only moves the customer Total / QuotationTotal, never the commission. (user 2026-07-28)
                const ccEditAddonRow = () => (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.5 }}>
                        <span style={{ fontSize: '0.74rem', fontWeight: 500, color: '#475569' }}>Add-on (financed)</span>
                        <TextField size="small" type="number" value={ccEditVal('addon')} onChange={e => onCcEdit('addon', e.target.value)} onWheel={e => e.target.blur()}
                            sx={{ width: 128 }} InputProps={{ sx: { fontFamily: "'JetBrains Mono',monospace", fontSize: '0.78rem' }, inputProps: { step: 0.01 } }} />
                    </Box>
                );
                // Editable pricing card — used for both CC and MIX (user 2026-07-22). `panelTitle`
                // is the header; `fieldSfx` is the per-row label suffix (' (CC)' or ' (Mix)').
                const editableCcPanel = (panelTitle = `${term}M Pricing (CC) · editable`, fieldSfx = ' (CC)') => (
                    <Box sx={{ flex: 1, p: 1.25, bgcolor: '#EFF6FF', border: '1px solid #86C7BC', borderRadius: 2, fontFamily: PP }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#1A8CFF', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                {panelTitle}
                            </Typography>
                            <Box component="span" sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.7, py: 0.15, bgcolor: '#DCFCE7', color: '#15803D', borderRadius: 1, fontSize: '0.58rem', fontWeight: 800, letterSpacing: '0.3px' }}>
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16A34A' }} /> COMMISSION
                            </Box>
                        </Box>
                        {ccEditRow(`Original Price${fieldSfx}`, 'original')}
                        {ccEditRow(`Astern Discount${fieldSfx}`, 'astern')}
                        {ccEditRow(`Agent Discount${fieldSfx}`, 'agent')}
                        {/* ALWAYS SHOWN, even at zero. These used to render only once the value
                            was already non-zero, which made them unreachable from here: the row
                            appeared only after the number existed, and the number could only be
                            typed into the row. Astern and Agent above are unconditional for the
                            same reason — a field that decides commission should not hide itself
                            because it happens to be zero today.

                            It matters more now that the breakdown deducts them: the team discount
                            comes off TOC and the upline discount off DOC, so a figure missing here
                            is money the overriders keep that they should have funded.
                            (user 2026-10-23: "this one need to have team discount too in the
                             create sales right") */}
                        {ccEditRow(`Upline Discount${fieldSfx}`, 'upline')}
                        {ccEditRow(`Team Discount${fieldSfx}`, 'team')}
                        {ccEditRow(`Final Price${fieldSfx}`, 'final', { strong: true, pays: true })}
                        {ccEditAddonRow()}
                        {isCommercial && row(dedOn ? `SST 6% (${discOf(ccBd) > 0 ? `− RM ${discOf(ccBd).toLocaleString()} deducted` : 'no discount yet'})` : 'SST 6% (on 20%)', sstPayableOf(ccBd), { muted: true })}
                        {row(isCommercial ? (dedOn ? 'Total Quotation (SST deducted)' : 'Total Quotation (with SST)') : 'Total Quotation', custTotalOf(ccBd), { strong: true, muted: true })}
                        <Typography sx={{ fontSize: '0.62rem', color: '#64748B', mt: 0.6 }}>
                            {ccOverride ? 'Edited — commission uses Original − Astern. Change the inputs above to re-derive.' : 'Auto-derived from cash. Edit any field to override; commission uses Original − Astern.'}
                        </Typography>
                    </Box>
                );
                return (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: ccBd ? 'row' : 'column' }, gap: 1.5 }}>
                        {panel('Cash', cashBd, !isCC)}
                        {ccBd && ((isCC || isMix) && canEditCcPricing
                            ? editableCcPanel(
                                isMix ? `MIX · ${term}M (Cash + CC) · editable` : `${term}M Pricing (CC) · editable`,
                                isMix ? ' (Mix)' : ' (CC)')
                            : panel(isMix ? `MIX · ${term}M (Cash + CC)` : `${term}M Credit Card`, ccBd, true, isMix ? '' : ' (CC)'))}
                    </Box>
                );
            })()}

            {/* SAVED pricing (Section 2) — shown when editing an existing sale and the
                cash Section 1 is left blank (untouched). These are the method-basis
                numbers stored at create; they REMAIN visible so the sale's payment-method
                pricing doesn't disappear on edit. Editing Section 1 above swaps to the
                live (recomputed) Section 2. */}
            {savedPricing && !salesPriceLens && !(Number(originalPrice) > 0) && (() => {
                const sOrig = Number(savedPricing.originalPrice) || 0;
                const sAstern = Number(savedPricing.asternDiscount) || 0;
                const sAgent = Number(savedPricing.agentDiscount) || 0;
                const sUpline = Number(savedPricing.uplineDiscount) || 0;
                const sTeam = Number(savedPricing.teamDiscount) || 0;
                const sAddon = Number(savedPricing.addOnPrice) || 0;
                const sFinal = (savedPricing.finalPrice != null)
                    ? Number(savedPricing.finalPrice)
                    // The fallback was dropping upline/team, so a snapshot without an explicit
                    // finalPrice overstated Final by whatever those two funded.
                    : Math.max(0, sOrig - sAstern - sAgent - sUpline - sTeam);
                const sTotal = sFinal + sAddon;
                if (sTotal <= 0 && sOrig <= 0) return null;
                const pm = String(savedPaymentMethod || '').toLowerCase();
                const isCC = pm.includes('12') || pm.includes('24') || pm.includes('36') || pm.includes('60');
                const term = pm.includes('60') ? 60 : pm.includes('36') ? 36 : pm.includes('24') ? 24 : pm.includes('12') ? 12 : 0;
                const row = (label, val, opts = {}) => {
                    const v = Number(val) || 0;
                    const isMarkup = opts.neg && v < 0;   // a negative discount = mark-up
                    const color = isMarkup ? '#16A34A' : (opts.neg ? '#B91C1C' : (opts.strong ? '#16A34A' : '#0F172A'));
                    return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: opts.strong ? '0.82rem' : '0.74rem', fontWeight: opts.strong ? 700 : 500, mt: opts.strong ? 0.4 : 0.15, pt: opts.strong ? 0.4 : 0, borderTop: opts.strong ? '1px solid #BFDBFE' : 'none' }}>
                        <span style={{ color: opts.strong ? '#0369A1' : '#475569' }}>{label}{isMarkup ? ' (mark-up)' : ''}</span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color }}>{isMarkup ? '+ ' : (opts.neg ? '− ' : '')}RM {Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                    </Box>
                    );
                };
                return (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 2, fontFamily: PP }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1A8CFF', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.6 }}>
                            Saved Pricing · {isCC ? `${term}M Credit Card` : 'Cash'} — current saved / quotation / commission
                        </Typography>
                        {row('Original Price', sOrig)}
                        {row('Astern Discount', sAstern, { neg: true })}
                        {row('Agent Discount', sAgent, { neg: true })}
                        {!!sUpline && row('Upline Discount', sUpline, { neg: true })}
                        {!!sTeam && row('Team Discount', sTeam, { neg: true })}
                        {row('Final Price', sFinal, { strong: true })}
                        {row('Add-on', sAddon)}
                        {(() => {
                            const _com = selectedCategory === 'COMMERCIAL';
                            const _ded = _com && !!deductSst;
                            const _disc = (Number(sAstern) || 0) + (Number(sAgent) || 0);
                            const _full = sstOn((Number(sOrig) || 0) + (Number(sAddon) || 0));
                            const _sst = _ded ? Math.max(0, _full - _disc) : (_com ? sstOn(sTotal) : 0);
                            const _excess = _ded ? Math.max(0, _disc - _full) : 0;
                            const _tot = _ded ? ((Number(sOrig) || 0) - _excess + (Number(sAddon) || 0) + _sst) : (sTotal + (_com ? sstOn(sTotal) : 0));
                            return (<>
                                {_com && row(_ded ? `SST 6% (${_disc > 0 ? `− RM ${_disc.toLocaleString()} deducted` : 'no discount'})` : 'SST 6% (on 20%)', _sst)}
                                {row(_com ? (_ded ? 'Total Quotation (SST deducted)' : 'Total Quotation (with SST)') : 'Total Quotation', _tot, { strong: true })}
                            </>);
                        })()}
                        <Typography sx={{ fontSize: '0.62rem', color: '#64748B', mt: 0.5 }}>This is the saved figure. To change it, enter a new cash price in Section 1 above.</Typography>
                    </Box>
                );
            })()}
        </>
    );
}
